'use client';
// Atomes UI GED : icônes inline, pastilles d'état, pill de comptage, eyebrow.
import React from 'react';
import { createPortal } from 'react-dom';
import { stateMeta, ISO_CLAUSES, TONES } from '@/lib/meta';
import { etatAbsence } from '@/lib/etats-absence';
import { useVerrouScroll } from '@/shared/verrouScroll';
import { useSortieFenetre, AlerteSortie } from '@/shared/fenetreModale';
import { styleBouton } from '@/shared/boutonStyles';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';

const GLY: Record<string, string> = {
  // `I` rend un SVG VIDE pour un nom inconnu (`GLY[n] || ''`) : un bouton
  // sans pictogramme, sans la moindre erreur. Ajouter le glyphe ICI est donc
  // la condition pour s'en servir ailleurs.
  pause: '<rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>',
  archive: '<rect x="2" y="3" width="20" height="5"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><line x1="10" y1="12" x2="14" y2="12"/>',
  // Pastille normative (F1) : un livre ouvert, distinct de `doc` (une feuille).
  book: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
  doc: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  dash: '<rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>',
  req: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  expand: '<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>',
  diff: '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>',
  // Miroir de `diff` (téléversement) : la flèche descend.
  // Vue à plat / vue en liste (26/08/2026). ⚠️ `I` rend un SVG VIDE pour un nom
  // inconnu : un bouton sans pictogramme, sans la moindre erreur. Les déclarer
  // ICI est la condition pour s'en servir ailleurs.
  table: '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/>',
  list: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
  download: '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="8 11 12 15 16 11"/><line x1="12" y1="15" x2="12" y2="2"/>',
  upload: '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 7 12 3 8 7"/><line x1="12" y1="3" x2="12" y2="15"/>',
  alert: '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  // ⚠️ `info` MANQUAIT ici alors qu'il existe dans `shared/ui/` : l'onglet
  // « Propositions » du plan de maintenance a rendu quatorze SVG vides — sans
  // erreur, sans rien à l'écran, exactement le défaut décrit juste au-dessus.
  // Seul l'avertissement de développement l'a montré. Dessin identique à celui
  // de la suite, pour que les trois modules disent la même chose.
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  gear: '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>',
  lock: '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  // Poignée de glisser-déposer (Lot 6 cartographie des processus, 05/09/2026) :
  // six points, le dessin conventionnel d'une poignée « saisir pour déplacer ».
  grip: '<circle cx="9" cy="6" r="1.4"/><circle cx="15" cy="6" r="1.4"/><circle cx="9" cy="12" r="1.4"/><circle cx="15" cy="12" r="1.4"/><circle cx="9" cy="18" r="1.4"/><circle cx="15" cy="18" r="1.4"/>',
  logout: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
  x: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  back: '<line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>',
  arrow: '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
  pen: '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  print: '<polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>',
  history: '<path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/>',
  // ⚠️ `journal` et `search` MANQUAIENT ici alors qu'ils existent dans
  // `shared/ui/Icon.tsx` : le bouton du journal, posé sur dix registres le
  // 18/08, n'a jamais affiché son pictogramme — un SVG vide, sans erreur, exact
  // répétition du défaut d'`info` noté plus haut. Constaté à l'écran sur la dev
  // le 24/08/2026. Dessins repris à l'identique du jeu partagé : le même nom
  // doit donner le même dessin dans les trois modules.
  journal: '<path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  send: '<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>',
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
  plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
  /* Retirer d'une case de planning — le pendant du « + » (RQ, 01/10/2026). */
  moins: '<line x1="5" y1="12" x2="19" y2="12"/>',
  /* ── Icônes propres à chaque entrée de navigation (14/08/2026) ────────────
     Les 48 entrées de la barre latérale se partageaient 14 glyphes : « check »
     servait à sept écrans sans rapport (critères d'acceptation, plan d'actions,
     saisie CQI, validation de méthode, habilitations, présences…). Une icône
     qui désigne sept choses ne désigne plus rien — elle cesse d'aider à
     retrouver un écran, et c'est précisément son seul rôle dans une barre
     repliée où le texte disparaît. Une entité, un glyphe. */
  // Documents & diffusion
  process: '<circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="12" r="2.5"/><circle cx="6" cy="18" r="2.5"/><path d="M8.5 6h4a3 3 0 0 1 3 3v.5"/><path d="M15.5 14.5v.5a3 3 0 0 1-3 3h-4"/>',
  stamp: '<path d="M9 3h6v4l2 3v3H7v-3l2-3z"/><rect x="5" y="16" width="14" height="4" rx="1"/>',
  // Écarts & amélioration
  filter: '<path d="M3 5h18l-7 8v6l-4 2v-8z"/>',
  capa: '<path d="M4 12a8 8 0 0 1 13.7-5.6"/><polyline points="18 3 18 7 14 7"/><path d="M20 12a8 8 0 0 1-13.7 5.6"/><polyline points="6 21 6 17 10 17"/>',
  bulb: '<path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5V16h8v-2.5A6 6 0 0 0 12 3z"/>',
  scale: '<path d="M12 3v18"/><path d="M6 7h12"/><path d="M6 7 3 14h6z"/><path d="M18 7l-3 7h6z"/>',
  target: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/>',
  lifebuoy: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path d="M5 5l4 4"/><path d="M15 15l4 4"/><path d="M19 5l-4 4"/><path d="M9 15l-4 4"/>',
  // Plateau technique
  ruler: '<rect x="2" y="8" width="20" height="8" rx="1"/><path d="M7 8v4"/><path d="M12 8v4"/><path d="M17 8v4"/>',
  thermo: '<path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z"/><path d="M12 9v6"/>',
  wrench: '<path d="M15 4a5 5 0 0 0-6.7 6.3L4 14.6 6.4 17l4.3-4.3A5 5 0 0 0 17 6l-2.6 2.6-2-2z"/>',
  // L'ENTITÉ équipement — un processeur, pas une roue ni une clé : la roue est
  // réservée au réglage et la clé à la maintenance (décision du 24/08/2026).
  automate: '<rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/>',
  chart: '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M21 20H3"/>',
  /* Famille « Matériel » (10/10/2026) : un carton ouvert — ce qu'on reçoit, stocke et consomme. */
  famMateriel: '<path d="M3 8l9-4 9 4-9 4z"/><path d="M3 8v9l9 4 9-4V8"/><path d="M12 12v9"/>',
  flask: '<path d="M9 3h6"/><path d="M10 3v6l-5.5 9A2 2 0 0 0 6.2 21h11.6a2 2 0 0 0 1.7-3L14 9V3"/><path d="M7.5 15h9"/>',
  dish: '<circle cx="12" cy="12" r="8"/><path d="M12 4v16"/><circle cx="9" cy="9" r="1"/><circle cx="15" cy="14" r="1"/>',
  droplet: '<path d="M12 3s6 6.5 6 10.5A6 6 0 0 1 6 13.5C6 9.5 12 3 12 3z"/>',
  atom: '<circle cx="12" cy="12" r="2"/><ellipse cx="12" cy="12" rx="9" ry="4"/><ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)"/><ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(120 12 12)"/>',
  sigma: '<path d="M18 5H6l6 7-6 7h12"/>',
  truck: '<rect x="1" y="7" width="13" height="9" rx="1"/><path d="M14 10h4l3 3v3h-7z"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
  handshake: '<path d="M8 12l3-3 2 2 3-3"/><path d="M3 9l4-3 5 2 5-2 4 3"/><path d="M3 9v5l5 4 3-3"/><path d="M21 9v5l-5 4-2-2"/>',
  wave: '<path d="M3 8c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/><path d="M3 16c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/>',
  /* EIL — échange inter-laboratoires (28/08/2026). Trois maisons reliées, et
     des flèches qui vont dans LES DEUX SENS : c'est ce qui le distingue du
     `handshake` de la sous-traitance, où l'échantillon part sans revenir. Ici
     chacun rend son résultat et reçoit celui des autres. */
  echange: '<circle cx="5" cy="6" r="2.4"/><circle cx="19" cy="6" r="2.4"/><circle cx="12" cy="19" r="2.4"/><path d="M7.4 6h9.2"/><path d="M6.1 8.1l4.7 8.8"/><path d="M17.9 8.1l-4.7 8.8"/>',
  // Personnel & plannings
  sitemap: '<rect x="9" y="2" width="6" height="4" rx="1"/><rect x="2" y="18" width="6" height="4" rx="1"/><rect x="16" y="18" width="6" height="4" rx="1"/><path d="M12 6v6"/><path d="M5 18v-3h14v3"/>',
  award: '<circle cx="12" cy="9" r="6"/><path d="M9 14l-1.5 7L12 19l4.5 2L15 14"/>',
  suitcase: '<rect x="2" y="7" width="20" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><path d="M2 13h20"/>',
  beach: '<circle cx="17" cy="6" r="3"/><path d="M3 20h18"/><path d="M8 20V9"/><path d="M4 12c1.5-3 5-4 8-2"/>',
  calendarUser: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18"/><path d="M8 3v4"/><path d="M16 3v4"/><circle cx="12" cy="15" r="2"/><path d="M9 19a3 3 0 0 1 6 0"/>',
  clipboardCheck: '<rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3h6v1"/><polyline points="9 12 11 14 15 10"/>',
  // Auto-évaluation de paillasse : une liste cochée SOUS une silhouette —
  // c'est quelqu'un qui se contrôle lui-même, et le dessin doit se distinguer
  // au premier coup d'œil de `clipboardCheck` (l'audit interne), sans quoi la
  // barre latérale suggérerait que les deux écrans font la même chose.
  selfCheck: '<circle cx="12" cy="5" r="2.5"/><path d="M7 11a5 5 0 0 1 10 0"/><polyline points="6 16 7.5 17.5 10 15"/><line x1="12" y1="16.5" x2="18" y2="16.5"/><polyline points="6 20 7.5 21.5 10 19"/><line x1="12" y1="20.5" x2="18" y2="20.5"/>',
  coins: '<ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v5c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/><path d="M4 11v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5"/>',
  logRh: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8"/><path d="M8 12h8"/><path d="M8 16h5"/>',
  matrix: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  // Triangle d'alerte — la NON-CONFORMITÉ. Distinct de `alert` (cercle), qui
  // reste au retrait d'urgence : deux alertes de nature différente.
  'alert-triangle': '<path d="M12 3 2 20h20z"/><path d="M12 9v5"/><path d="M12 17h.01"/>',
  /* ── Icônes des FAMILLES de la barre ────────────────────────────────────
     Une famille et l'une de ses entrées ne peuvent pas porter le même glyphe :
     côte à côte dans la barre dépliée, le titre de rubrique et sa première
     ligne devenaient indiscernables. Ces six-là sont donc PROPRES aux
     familles, et n'apparaissent nulle part ailleurs. */
  famDocuments: '<path d="M4 4h9l3 3h4v13H4z"/><path d="M8 12h8"/><path d="M8 16h5"/>',
  famEcarts: '<path d="M3 17l5-6 4 4 4-6 5 5"/><circle cx="8" cy="11" r="1.5"/><circle cx="16" cy="9" r="1.5"/>',
  famPilotage: '<circle cx="12" cy="12" r="9"/><path d="M12 12l4.5-4.5"/><path d="M12 3v2"/><path d="M21 12h-2"/><path d="M12 21v-2"/><path d="M3 12h2"/>',
  /* ⚠️ « famTechnique » COIFFE DÉSORMAIS LE PARC, et non plus les dix-sept
     entrées du plateau : la famille a été scindée le 12/09/2026 (retour 023).
     Le glyphe — un appareil — décrivait déjà mieux les équipements que les
     cartes de contrôle qui l'accompagnaient. */
  famTechnique: '<rect x="3" y="9" width="18" height="11" rx="2"/><path d="M8 9V6a4 4 0 0 1 8 0v3"/><circle cx="12" cy="14" r="2"/>',
  /* La moitié « contrôles & performance » : une courbe avec ses limites — c'est
     ce qu'on vient y lire, et cela ne se confond avec aucun autre glyphe de la
     barre (`famEcarts` est une ligne brisée SANS limites, et il dit les
     non-conformités). */
  famControles: '<path d="M3 6h18"/><path d="M3 18h18"/><path d="M4 12h3l2.5-4 3 8 2.5-4h5"/>',
  famPersonnel: '<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><circle cx="17" cy="9" r="2.5"/><path d="M16 20a5 5 0 0 1 5-5"/>',
  famPlannings: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M8 14h3"/>',
  // Chantier manuel-vitrine — un tube de prélèvement ouvert sur son livret.
  famManuel: '<path d="M9 3h6"/><path d="M10 3v8.5a2 2 0 1 1-4 0"/><path d="M10 3v8.5"/><path d="M6 11.5V3"/><path d="M14 8h7v13h-7a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z"/><path d="M17 12h2"/><path d="M17 15h2"/>',
  // MoMent — une silhouette de grossesse : la tête, le buste et le ventre.
  famMoment: '<circle cx="11" cy="4.5" r="2.2"/><path d="M8.5 21v-5.5"/><path d="M8.5 15.5V9.5a2.5 2.5 0 0 1 5 0v1a4 4 0 0 1 0 8h-5"/><path d="M13.5 21v-2.5"/>',
  tube: '<path d="M8 2h8"/><path d="M10 2v13a2 2 0 1 0 4 0V2"/><path d="M10 9h4"/>',
  // Balance à deux plateaux — la CALIBRATION, distincte de `scale` (risques).
  scale2: '<path d="M12 4v16"/><path d="M8 20h8"/><circle cx="12" cy="4" r="1.5"/><path d="M4 9h16"/><path d="M4 9l-2 5a3 3 0 0 0 6 0z"/><path d="M20 9l2 5a3 3 0 0 1-6 0z"/>',
  // Grille de compétences — habilitations RH, distincte de `matrix` (postes).
  matrix2: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/><polyline points="12 14 14 16 18 12"/>',

  // Maillon de chaîne — « copier le lien » d'une fiche (adressage du 14/08/2026).
  // ⚠️ Un nom absent de cette table rend un SVG VIDE, sans erreur : le bouton
  // paraît simplement amputé de son icône. C'est pour cela qu'on l'ajoute ici
  // plutôt que de se rabattre sur un glyphe voisin au sens approximatif.
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>',
  trash: '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/>',
  clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  eye: '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>',
  refresh: '<path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v5h5"/>',
  // Navigation période par période (semaine, mois) dans le module RH.
  'chevron-left': '<polyline points="15 18 9 12 15 6"/>',
  'chevron-right': '<polyline points="9 18 15 12 9 6"/>',
  // Chevron des listes déroulantes cherchables — même dessin que celui posé en
  // CSS sur les `select` natifs (globals.css), pour que les deux formes du
  // composant `Selecteur` se ressemblent.
  'chevron-bas': '<polyline points="6 9 12 15 18 9"/>',
  // Plan du laboratoire (lot N5) — carte pliée pour le référentiel des
  // emplacements, épingle pour un endroit précis. Sans ces deux glyphes, la
  // section du paramétrage aurait rendu des boutons muets : `I` ne lève pas
  // d'erreur sur un nom inconnu, il dessine un SVG vide.
  map: '<polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21"/><line x1="8" y1="3" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="21"/>',
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
  // Personnel — en-tête de la famille « Personnel & plannings » de la barre.
  // `req` (bulle de dialogue) y servait par défaut d'icône de l'organigramme :
  // il ne dit rien de l'humain.
  users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  // Lien qui QUITTE l'application — une flèche qui sort d'un cadre ouvert.
  'lien-externe': '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>',
  // Assistance IA — étincelle, distincte de `gear` (paramétrage).
  sparkle: '<path d="M12 3v5"/><path d="M12 16v5"/><path d="M3 12h5"/><path d="M16 12h5"/><path d="M6 6l3 3"/><path d="M15 15l3 3"/><path d="M18 6l-3 3"/><path d="M9 15l-3 3"/>',
};

export function I({ n, s = 16, sw = 2, style }: { n: string; s?: number; sw?: number; style?: React.CSSProperties }) {
  // Un nom inconnu rend un SVG vide — un bouton sans pictogramme, sans erreur.
  // Quatre glyphes ont manqué des mois durant sans que rien ne le signale. En
  // développement on le dit ; en production on garde le rendu vide, un bouton
  // sans icône valant mieux qu'un écran cassé.
  if (process.env.NODE_ENV !== 'production' && !GLY[n]) {
    console.warn(`[ui] Icône « ${n} » absente du jeu de glyphes : rendu vide. Ajoutez-la dans GLY (ui.tsx).`);
  }
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}
      strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flex: '0 0 auto', ...style }}
      dangerouslySetInnerHTML={{ __html: GLY[n] || '' }} />
  );
}

/* ── LA MARQUE DE L'ASSISTANT (20/10/2026) ──────────────────────────────────
 * Partout où un assistant (IA) parle ou propose, il porte la couleur `--ia-*`
 * et seulement lui : on voit d'un coup d'œil ce qui vient de la machine, et
 * qu'il faut relire. Un écran n'écrit pas `<I n="sparkle">` lui-même : il
 * appelle `MarqueIA` (cliquet : qualite-marque-ia). */

/** L'étincelle de l'assistant, à sa couleur. */
export function MarqueIA({ s = 14 }: { s?: number }) {
  return <I n="sparkle" s={s} style={{ color: 'var(--ia-accent)' }} />;
}

/** La pastille ronde de l'assistant, quand il PARLE (une bulle, un en-tête). */
export function AvatarIA({ s = 30 }: { s?: number }) {
  return (
    <span aria-hidden style={{
      flex: '0 0 auto', width: s, height: s, borderRadius: 'var(--radius-pill)',
      background: 'var(--ia-accent)', color: 'var(--text-on-primary)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <I n="sparkle" s={Math.round(s / 2)} />
    </span>
  );
}

/** Le bouton principal d'un échange avec l'assistant (sur un `<Bouton
 *  variante="primaire">`). */
export const boutonIAPlein: React.CSSProperties = {
  background: 'var(--ia-accent)', borderColor: 'var(--ia-accent)', color: 'var(--text-on-primary)',
};

/** Un bouton qui DEMANDE quelque chose à l'assistant (à poser en `style` d'un
 *  `<Bouton>` contour ou discret). */
export const boutonIA: React.CSSProperties = {
  color: 'var(--ia-encre)', borderColor: 'var(--ia-bord)', background: 'var(--ia-surface)',
};

/** Le cadre de ce que l'assistant propose ou explique. */
export const cadreIA: React.CSSProperties = {
  background: 'var(--ia-surface)', border: '1px solid var(--ia-bord)',
};

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    /* ⚠️ LE TEXTE DANS UNE SEULE BOÎTE (19/10/2026). Posés directement dans la
       rangée flexible, « PILOTAGE · », « ISO 15189 » et « §8.6 » devenaient
       chacun une colonne : sur un téléphone ils s'empilaient en escalier, et
       la rangée, incapable de passer à la ligne, élargissait la page. Le trait
       reste à gauche ; le texte, lui, passe à la ligne comme du texte. */
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: '100%', fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, letterSpacing: 'var(--tracking-eyebrow)', textTransform: 'uppercase', color: 'var(--color-primary-active)' }}>
      <span style={{ flex: 'none', width: 18, height: 2, background: 'var(--color-primary)', borderRadius: 2 }} />
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

/**
 * LA PASTILLE — le seul badge de statut (L9, 11/10/2026 ; É-12 de l'audit des tableaux).
 *
 * Neuf écrans redessinaient chacun leur badge : taille, rayon et paire fond/texte
 * redécidés à chaque fois. Ils se construisent désormais ici : un `ton` de `TONES`
 * (`success`, `warning`, `danger`, `info`, `muted`, `neutral`) OU une paire
 * `bg`/`fg` quand la couleur porte un sens métier propre (niveau de péremption,
 * nature d'une grille). Même rayon (`--radius-pill`), même graisse, même
 * hauteur que `StatePill`, qui n'est que la pastille des états documentaires.
 * `compacte` : 11 px, pour les listes denses.
 */
export function Pastille({ ton, bg, fg, title, compacte, children }: {
  ton?: keyof typeof TONES; bg?: string; fg?: string; title?: string; compacte?: boolean; children: React.ReactNode;
}) {
  const t = ton ? TONES[ton] : null;
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: compacte ? '2px 9px' : '3px 10px', borderRadius: 'var(--radius-pill)',
      background: bg ?? t?.bg ?? 'var(--gray-50)', color: fg ?? t?.fg ?? 'var(--text-muted)',
      fontSize: compacte ? 11 : 12.5, fontWeight: 700, whiteSpace: 'nowrap', cursor: title ? 'help' : undefined,
    }}>
      {children}
    </span>
  );
}

export function StatePill({ statut }: { statut: string }) {
  const m = stateMeta(statut);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.fg }} />{m.label}
    </span>
  );
}

/**
 * L'ÉTAT D'UNE ABSENCE — vocabulaire propre (05/10/2026).
 *
 * ⚠️ NE PAS employer `StatePill` pour une absence : il porte les états du
 * CIRCUIT DOCUMENTAIRE, et `validee` n'en est pas un. C'est le mélange des
 * deux qui faisait lire « Brouillon » sur un congé validé dans « Mon
 * planning ». Le vocabulaire vit dans `@/lib/etats-absence`, module pur,
 * éprouvé par `npm test`.
 */
export function StatePillAbsence({ statut }: { statut: string }) {
  const e = etatAbsence(statut);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: e.fond, color: e.texte, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: e.texte }} />{e.label}
    </span>
  );
}

export function CountPill({ n, color = 'var(--amber-500)' }: { n: number; color?: string }) {
  if (n <= 0) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 'var(--radius-pill)', background: color, color: '#fff', fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{n}</span>
  );
}

/* ── Les trois points d'entrée historiques des boutons ─────────────────────
 *
 * Ce ne sont plus que des ALIAS de `styleBouton` (shared/ui/boutonStyles.ts),
 * qui est la seule définition de l'apparence d'un bouton (règles :
 * shared/docs/UI-CONVENTIONS.md). Signatures et appelants inchangés ; le code
 * NEUF utilise `Bouton` et `PiedActions`.
 *
 * ⚠️ Vocabulaire : `'primary'` = l'action principale, désormais PLEINE (elle était
 * pâle avec `btnSmall('primary')`) ; `'outline'` = `contour` ; `'danger'` = `danger`.
 * Ne pas ajouter de style ici : toute exception se règle dans `styleBouton`. */
export const btnPrimary: React.CSSProperties = styleBouton('primaire', 'md');
export const btnOutline: React.CSSProperties = styleBouton('contour', 'md');
export const btnSmall = (variant: 'primary' | 'outline' | 'danger'): React.CSSProperties => (
  styleBouton(variant === 'primary' ? 'primaire' : variant === 'danger' ? 'danger' : 'contour', 'sm')
);
/* ═══════════════════════════════════════════════════════════════════════════
 * UN BOUTON QUI SE COMPREND À SON ICÔNE N'ÉCRIT PAS L'ACTION (Inès, 28/08/2026).
 *
 * « Si une icône suffit à comprendre à quoi sert le bouton, n'écris pas
 * l'action. Une icône claire vaut mieux qu'un bouton large. »
 *
 * ── Ce que la règle vise ───────────────────────────────────────────────────
 *
 * Les colonnes d'actions d'un registre alignent le même verbe sur chaque ligne
 * — quarante fois « Modifier », quarante fois « Supprimer ». Le mot n'apprend
 * rien après la première ligne, et il prend la place de la donnée : sur les
 * tableaux serrés, c'est lui qui pousse les titres à l'ellipsis.
 *
 * ── Et ce qu'elle NE vise PAS ──────────────────────────────────────────────
 *
 * ⚠️ Une icône n'est claire que si son geste est UNIVOQUE. Le crayon dit
 * « modifier », la corbeille dit « supprimer », la roue dit « régler » — trois
 * gestes que tout le monde lit sans hésiter. Mais « Détacher », « Désactiver »
 * et « Archiver » cohabitent dans ce module, ne veulent pas dire la même chose,
 * et aucun pictogramme ne les distingue : elles GARDENT leur mot. Remplacer un
 * mot par une icône ambiguë ne rend pas le bouton plus petit, il le rend muet.
 *
 * ⚠️ Le `title` n'est PAS l'étiquette. Il apparaît après une seconde, n'existe
 * pas au tactile, et un lecteur d'écran a besoin d'`aria-label`. Les deux sont
 * donc OBLIGATOIRES ici — c'est ce qui rend l'icône seule acceptable, et c'est
 * la raison d'être de ce composant plutôt qu'un `<button>` où l'on oublierait
 * l'un des deux une fois sur trois.
 * ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
 * L'INFOBULLE DU RAIL REPLIÉ — au PORTAIL, sinon elle est rognée.
 *
 * ── Ce qu'elle répare (relevé du 27/08/2026, P4a) ──────────────────────────
 *
 * Replié, le rail de navigation aligne 48 icônes de 44 px sans un mot, avec un
 * simple `title` en guise d'étiquette. Le `title` natif apparaît après environ
 * une seconde, ne se style pas, et n'existe pas au tactile : sur 48 entrées
 * dont plusieurs se ressemblent (deux enveloppes, plusieurs triangles), c'est
 * ce qui fait qu'on ne retrouve pas un écran.
 *
 * ── Pourquoi un portail, et pas un `::after` ───────────────────────────────
 *
 * La colonne de navigation est ELLE-MÊME une zone de défilement
 * (`.ged-nav-colonne`, `overflow-y: auto`). Une infobulle dessinée dedans, en
 * `position: absolute` ou en `::after`, est rognée par cette boîte sur les deux
 * axes — et une infobulle coupée est pire que le `title` natif qu'elle
 * remplace. Rendue au portail dans `<body>`, elle se dessine par-dessus tout.
 *
 * ── Le délai est court, mais pas nul ───────────────────────────────────────
 *
 * ~150 ms : assez pour ne pas clignoter quand la souris traverse le rail,
 * assez peu pour répondre quand on s'arrête. Le `title` natif attend une
 * seconde, ce qui est le temps qu'il faut pour renoncer.
 *
 * ⚠️ `aria-hidden` : le bouton porte DÉJÀ son nom accessible (`aria-label`).
 * Une infobulle annoncée en plus ferait lire l'intitulé deux fois.
 * ═══════════════════════════════════════════════════════════════════════════ */
export function useInfobulle(texte: string, actif = true) {
  const [ancre, setAncre] = React.useState<{ x: number; y: number } | null>(null);
  const minuteur = React.useRef<number | null>(null);

  const annuler = React.useCallback(() => {
    if (minuteur.current !== null) { window.clearTimeout(minuteur.current); minuteur.current = null; }
    setAncre(null);
  }, []);

  const montrer = React.useCallback((e: React.MouseEvent | React.FocusEvent) => {
    if (!actif) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // La position est prise TOUT DE SUITE : à l'expiration du minuteur, la
    // souris a pu partir et l'élément défiler.
    const cible = { x: r.right + 8, y: r.top + r.height / 2 };
    if (minuteur.current !== null) window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(() => setAncre(cible), 150);
  }, [actif]);

  React.useEffect(() => () => {
    if (minuteur.current !== null) window.clearTimeout(minuteur.current);
  }, []);

  const bulle = ancre && typeof document !== 'undefined' ? createPortal(
    <div aria-hidden style={{
      position: 'fixed', left: ancre.x, top: ancre.y, transform: 'translateY(-50%)',
      zIndex: COUCHES.TRAVAIL - 1,
      background: 'var(--navy-900, #0E2740)', color: '#fff',
      fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
      padding: '6px 10px', borderRadius: 'var(--radius-sm)',
      boxShadow: 'var(--shadow-md)', whiteSpace: 'nowrap', pointerEvents: 'none',
    }}>{texte}</div>,
    document.body,
  ) : null;

  return {
    bulle,
    /* À étaler sur l'élément survolé. `focus`/`blur` autant que la souris : au
       clavier aussi on a besoin de savoir où l'on est. */
    liens: {
      onMouseEnter: montrer,
      onMouseLeave: annuler,
      onFocus: montrer,
      onBlur: annuler,
    },
  };
}

export function BoutonIcone({ icone, action, onClick, variante = 'outline', disabled, style, taille = 14 }: {
  /** Glyphe du jeu partagé — `pen`, `trash`, `gear`, `x`. */
  icone: string;
  /** Ce que fait le bouton, EN TOUTES LETTRES : « Modifier BIO24-NC-2026-014 ».
   *  Sert d'infobulle ET de nom accessible. Jamais « Modifier » tout seul —
   *  quarante boutons nommés pareil ne se distinguent pas au clavier. */
  action: string;
  onClick: () => void;
  variante?: 'primary' | 'outline' | 'danger';
  disabled?: boolean;
  style?: React.CSSProperties;
  taille?: number;
}) {
  return (
    <button type="button" className="ged-btn" onClick={onClick} disabled={disabled}
      title={action} aria-label={action}
      style={{
        ...btnSmall(variante),
        // Carré : sans largeur imposée, le `padding` horizontal de `btnSmall`
        // dessinerait un rectangle autour d'une icône de 14 px.
        padding: 0, width: 30, minWidth: 30, justifyContent: 'center',
        opacity: disabled ? 0.45 : 1,
        ...style,
      }}>
      <I n={icone} s={taille} />
    </button>
  );
}

/**
 * Le bouton de CRÉATION d'une liste — « + Nouvel examen », « + Nouveau
 * paramètre », « + Déclarer une technique ».
 *
 * Un seul composant pour les trois (retour Ines du 19/08) : chaque liste avait
 * dessiné le sien, l'une en bouton de marque sombre, l'autre en cyan, l'une
 * dans l'en-tête de page, l'autre au-dessus du tableau. À l'écran ils ne se
 * ressemblaient plus, et on ne savait pas où le chercher en changeant d'onglet.
 *
 * La règle tient en deux points, et c'est le composant qui les porte plutôt que
 * chaque appelant : fond cyan clair avec un « + » en tête, et une place fixe —
 * EN HAUT À DROITE du tableau, en face de sa barre de recherche. Poser le
 * bouton ailleurs demande de ne pas utiliser ce composant, ce qui se voit.
 */
export function BoutonCreer({ children, onClick, title, disabled, style }: {
  children: React.ReactNode; onClick: () => void; title?: string; disabled?: boolean;
  /** Marges d'insertion UNIQUEMENT — couleurs et typographie restent celles de
   *  la maison, c'est toute la raison d'être de ce composant. */
  style?: React.CSSProperties;
}) {
  return (
    <button type="button" className="ged-btn" title={title} disabled={disabled}
      /* `btnPrimary` — le bleu azur PLEIN, texte blanc, qui est le bouton
         d'action de toute l'application. Une première version utilisait le
         cyan pâle de `btnSmall('primary')` : plus discret, mais ce n'est pas
         la règle de la maison, et un bouton de création qui ne ressemble pas
         aux autres boutons de création ne se trouve pas (RQ, 19/08). */
      style={{ ...btnPrimary, marginLeft: 'auto', whiteSpace: 'nowrap', padding: '8px 14px', fontSize: 13, ...style }}
      onClick={onClick}>
      {/* `aria-hidden` : le « + » est un ornement, le libellé dit déjà l'action.
          Lu à voix haute il donnerait « plus nouvel examen ». */}
      <span aria-hidden style={{ fontSize: 15, lineHeight: 1, fontWeight: 700 }}>+</span>
      {children}
    </button>
  );
}

/* ── Choisir PLUSIEURS entrées : pastilles + recherche ─────────────────────
 *
 * ⚠️ LE MÊME GESTE AVAIT DEUX INTERFACES OPPOSÉES SUR UN SEUL ONGLET.
 *
 * Le bloc « Paramètres de l'examen » avait abandonné la sélection multiple au
 * Ctrl-clic — « c'est bizarre comme mode » (RQ, 18/08/2026) : un mode que
 * personne ne connaît, et où un clic SANS Ctrl efface d'un coup tout ce qui
 * était choisi. Il avait été remplacé par des pastilles qu'on retire à la croix
 * et une recherche qui ajoute au clic.
 *
 * Deux blocs plus bas, sur le même onglet, « Composantes » et « Répertoire »
 * étaient restés des `<select multiple>` : le geste rejeté cohabitait avec son
 * remplacement. Et c'est le RÉPERTOIRE qui coûtait le plus cher — un
 * antibiogramme y porte quarante molécules, qu'un clic malheureux vidait.
 *
 * L'interface est donc écrite UNE fois et servie aux trois blocs. C'est la
 * recopie qui avait laissé les deux moitiés de l'écran diverger.
 *
 * La recherche ignore les accents, comme celle de `Selecteur` : sans quoi
 * « proteine » ne trouve pas « Protéine C réactive », et l'on conclut que
 * l'entrée n'existe pas. */
export function SelecteurPastilles({ options, choisis, onChange, placeholder, vide, modifiable, ariaLabel, unite = 'entrée', mono = true }: {
  options: { documentId: string; code: string; nom: string }[];
  choisis: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
  /** Ce qu'on lit quand rien n'est choisi — la phrase dit ce que ça SIGNIFIE,
   *  et non « aucun » : ne rien rattacher est une décision, pas un vide. */
  vide: React.ReactNode;
  modifiable: boolean;
  ariaLabel: string;
  unite?: string;
  /**
   * Le libellé est-il un CODE ?
   *
   * Vrai par défaut — les trois premiers appelants choisissent des analytes et
   * des examens, dont le code (`HB1`, `NFS`) se lit en chasse fixe. Faux quand
   * l'entrée est une phrase : « Pré-analytique », « Responsable qualité ». En
   * chasse fixe, une phrase française se lit comme un identifiant technique, et
   * l'on cherche un sens à sa casse et à ses espaces (12/09/2026).
   */
  mono?: boolean;
}) {
  const [recherche, setRecherche] = React.useState('');
  /* ⚠️ LA LISTE S'OUVRE À LA PRISE DE FOCUS, ET PLUS SEULEMENT À LA FRAPPE
     (12/09/2026). Elle ne s'ouvrait qu'au premier caractère tapé : sur un champ
     vide, rien ne disait qu'il y avait une liste derrière, ni ce qu'elle
     contenait. C'est tenable pour choisir un analyte parmi deux cents — on sait
     ce qu'on cherche — mais pas pour les douze processus du laboratoire, qu'on
     ne peut pas deviner.
     Demande de la RQ (11/09) : « je voudrais que ce soit une liste déroulante,
     quand on saisit une entrée elle s'affiche et on peut saisir à nouveau une
     nouvelle entrée et ainsi de suite ». Une liste qui ne s'ouvre qu'à
     l'aveugle n'est pas une liste déroulante. */
  const [focus, setFocus] = React.useState(false);
  /* `useId` : deux blocs de l'onglet portent ce composant en même temps, et
     `aria-controls` doit désigner LEUR liste, pas celle du voisin. */
  const idListe = React.useId();
  const champ = React.useRef<HTMLInputElement | null>(null);
  const liste = React.useRef<HTMLDivElement | null>(null);
  const sansAccent = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const q = sansAccent(recherche.trim());
  /* Champ VIDE : on propose ce qui reste à choisir. Le plafond de huit tient :
     au-delà, la liste défile — et huit lignes suffisent à faire comprendre
     qu'il y a une liste, ce qui est tout ce qui manquait. */
  /* Le CODE EXACT d'abord, puis les codes qui commencent par la saisie, puis
     le reste (relevé du 28/10/2026 : « gly » rangeait GLY derrière les
     anticorps anti-β2-glycoprotéine, dans l'ordre alphabétique). */
  const rang = (o: { code: string }) => {
    const c = sansAccent(o.code);
    return c === q ? 0 : c.startsWith(q) ? 1 : 2;
  };
  const suggestions = options
    .filter((o) => !choisis.includes(o.documentId))
    .filter((o) => !q || sansAccent(`${o.code} ${o.nom}`).includes(q))
    .sort((a, b) => (q ? rang(a) - rang(b) : 0))
    .slice(0, 8);
  /* Échap REFERME la liste sans effacer la saisie (28/10/2026) ; un second
     Échap, liste fermée, vide le champ. Toute frappe la rouvre. */
  const [replie, setReplie] = React.useState(false);
  const ouverte = modifiable && !replie && (focus || recherche.trim().length > 0);

  /* ⚠️ LA LISTE EST RENDUE AU PORTAIL, ET NON EN `position: absolute`.
     Le bloc « Répertoire » est le dernier de l'onglet, tout au bas d'une zone
     qui DÉFILE (le corps de `Modale`, en `overflow: auto`). Une liste posée en
     absolu y est rognée par le bas de cette zone : sur le bloc du haut on
     voyait ses huit propositions, sur celui du bas une bande de deux lignes —
     et rien ne disait que la liste continuait. Au portail, elle se dessine
     par-dessus la fenêtre.
     La contrepartie d'un portail est qu'il ne suit plus son champ : la position
     est donc RECALCULÉE au défilement et au redimensionnement. L'écouteur est
     posé en phase de CAPTURE (`true`) parce que la fenêtre défile dans sa
     propre zone et non sur `window`, qui ne verrait rien passer.
     Elle BASCULE AU-DESSUS du champ quand le dessous manque — sinon le dernier
     bloc de l'onglet rouvrait le même défaut, hors de la fenêtre cette fois. */
  const [ancre, setAncre] = React.useState<{ gauche: number; largeur: number; haut?: number; bas?: number; hauteur: number } | null>(null);
  /* La mesure est appelée par la FRAPPE puis par le défilement, et jamais
     depuis le corps d'un effet : un `setState` synchrone dans un effet relance
     un rendu en cascade, et la règle du projet le refuse. La frappe est le seul
     geste qui ouvre la liste, donc le seul moment où la première mesure est
     nécessaire ; l'effet ne fait plus qu'ABONNER. */
  const mesurer = React.useCallback(() => {
    const r = champ.current?.getBoundingClientRect();
    if (!r) return;
    const dessous = window.innerHeight - r.bottom - 12;
    const dessus = r.top - 12;
    const versLeHaut = dessous < 150 && dessus > dessous;
    setAncre({
      gauche: r.left, largeur: r.width,
      haut: versLeHaut ? undefined : r.bottom + 4,
      bas: versLeHaut ? window.innerHeight - r.top + 4 : undefined,
      hauteur: Math.max(96, Math.min(240, versLeHaut ? dessus : dessous)),
    });
  }, []);
  React.useEffect(() => {
    if (!ouverte) return undefined;
    window.addEventListener('scroll', mesurer, true);
    window.addEventListener('resize', mesurer);
    return () => {
      window.removeEventListener('scroll', mesurer, true);
      window.removeEventListener('resize', mesurer);
    };
  }, [ouverte, mesurer]);

  /* Refermer au clic AILLEURS : sortie du flux, la liste ne se ferme plus
     d'elle-même en perdant le survol. On écoute `mousedown` et non `click` —
     un `click` se déclenche sur l'ancêtre commun du press et du release, et
     glisser depuis la liste vers la page l'aurait refermée. */
  React.useEffect(() => {
    if (!ouverte) return undefined;
    const dehors = (e: MouseEvent) => {
      const c = e.target as Node;
      if (champ.current?.contains(c) || liste.current?.contains(c)) return;
      /* ⚠️ `setFocus(false)` EN PLUS de vider la recherche : depuis que le focus
         ouvre la liste, effacer le texte ne la referme plus. Sans cette ligne,
         un clic à côté laissait la liste posée au-dessus de la page. */
      setRecherche(''); setFocus(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouverte]);

  return (
    <div>
      {/* La zone des pastilles DÉFILE au-delà de sa hauteur : les quarante
          molécules d'un antibiogramme repoussaient la recherche — donc le seul
          moyen d'en ajouter une — hors de la fenêtre. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6, maxHeight: 132, overflowY: 'auto' }}>
        {choisis.map((id) => {
          const o = options.find((x) => x.documentId === id);
          const nom = o?.code || '…';
          return (
            <span key={id} title={o?.nom || ''}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontFamily: mono ? 'var(--font-mono)' : 'inherit', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--surface-card, #fff)' }}>
              {nom}
              {modifiable && (
                /* La croix vient du JEU DE GLYPHES, et non du caractère « ✕ » :
                   posé en texte, il se rend dans la police du système — d'une
                   graisse et d'une taille étrangères aux autres croix de
                   l'application, celle de `Modale` en tête. */
                <button type="button" className="ged-btn"
                  onClick={() => onChange(choisis.filter((x) => x !== id))}
                  title={`Retirer ${nom}`} aria-label={`Retirer ${nom}`}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'inline-flex' }}>
                  <I n="x" s={12} />
                </button>
              )}
            </span>
          );
        })}
        {!choisis.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{vide}</span>}
      </div>
      {modifiable && (
        <div style={{ maxWidth: 380 }}>
          <input ref={champ} value={recherche} placeholder={placeholder} aria-label={ariaLabel}
            role="combobox" aria-expanded={ouverte} aria-autocomplete="list" aria-controls={idListe}
            onChange={(e) => { setRecherche(e.currentTarget.value); setReplie(false); mesurer(); }}
            /* Échap referme la LISTE, pas la fenêtre. `stopPropagation` arrête
               l'événement avant qu'il n'atteigne l'écouteur de `window` que
               pose `Modale` — sans quoi une seule frappe fermait les deux, et
               l'on perdait la page pour avoir voulu abandonner une recherche.
               Même geste que l'édition d'un champ du dossier, plus bas. */
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              if (ouverte) { e.stopPropagation(); setReplie(true); return; }
              if (recherche) { e.stopPropagation(); setRecherche(''); }
            }}
            /* Le focus ouvre ; le `blur` NE FERME PAS — cliquer une proposition
               fait perdre le focus au champ avant que le clic n'aboutisse, et
               la liste se serait dérobée sous le doigt. C'est le clic DEHORS
               (plus haut) et Échap qui referment. */
            onFocus={() => { setFocus(true); setReplie(false); mesurer(); }}
            onClick={() => { if (replie) { setReplie(false); mesurer(); } }}
            style={{ ...inputStyle, fontSize: 12.5 }} />
        </div>
      )}
      {ouverte && ancre && createPortal(
        <div ref={liste} id={idListe} role="listbox" aria-label={ariaLabel}
          style={{
            position: 'fixed', zIndex: 420,
            left: ancre.gauche, width: ancre.largeur, top: ancre.haut, bottom: ancre.bas,
            maxHeight: ancre.hauteur, overflowY: 'auto',
            background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-xl, 0 10px 30px rgba(14,39,64,.18))',
          }}>
          {/* Une liste vide ne disait RIEN : on ne savait pas si l'entrée
              manquait au référentiel ou si elle était déjà choisie. */}
          {suggestions.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--text-muted)' }}>
              {options.length && choisis.length >= options.length
                ? `Tout est déjà choisi : ${options.length} ${unite}${options.length > 1 ? 's' : ''} sur ${options.length}.`
                : `Rien ne correspond — ou l’${unite} est déjà choisie.`}
            </div>
          )}
          {suggestions.map((o) => (
            <button key={o.documentId} type="button" role="option" aria-selected={false} className="ged-btn"
              onClick={() => { onChange([...choisis, o.documentId]); setRecherche(''); }}
              style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}>
              {/* `aria-hidden` : le « + » est un ornement, le libellé dit
                  déjà l'action. Lu à voix haute, « plus HB1 » — même règle
                  que `BoutonCreer`. */}
              <span aria-hidden style={{ fontWeight: 700 }}>+</span>
              <span style={{ fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{o.code}</span>
              {/* Le nom n'est répété que s'il dit autre chose que le code : sur
                  une entrée dont les deux sont la même phrase, « Pré-analytique
                  Pré-analytique » se lit comme un défaut d'affichage. */}
              {o.nom !== o.code && <span style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.nom}</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
      {choisis.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
          {choisis.length} {unite}{choisis.length > 1 ? 's' : ''} — la croix retire, la liste ajoute.
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * L'INTITULÉ DE RUBRIQUE — le petit texte en capitales grises qui coiffe un
 * bloc. Une seule définition, parce qu'il y en avait SEPT.
 *
 * ── Ce qui a été mesuré (fenêtre d'un examen, 26/08/2026) ──────────────────
 *
 *   la maison (`lbl`)          11    0.04em   marge 5
 *   Réglages                   10.5  0.04em   marge 0
 *   Interprétation             10.5  0.04em   —
 *   Performance                11    AUCUN    marge 4
 *   Liaisons (deux endroits)   11    0.03em   marge 5
 *   Techniques (fenêtre)       11    0.04em   marge 4
 *   Liaisons (une rangée)      11.5  0.03em   —
 *
 * Aucune de ces valeurs n'est fautive prise seule. C'est leur DIVERGENCE qui se
 * voit — non pas dans un onglet, mais en passant de l'un à l'autre, où le même
 * élément change de taille et d'assise. Exactement le défaut que `BarreEntete`
 * a corrigé pour la barre du site et du manuel, et pour la même raison :
 * plusieurs définitions du même objet redérivent, toujours.
 *
 * `lbl` (`screens/ressources/commun.tsx`) porte ces valeurs depuis 47 fichiers
 * et fait donc autorité ; il n'est pas déplacé — il RÉFÉRENCE cette constante,
 * pour que les deux ne puissent plus diverger.
 * ═══════════════════════════════════════════════════════════════════════════ */
export const styleRubrique: React.CSSProperties = {
  // 12 px et non 11 (relevé du 27/08/2026, P5b) : c'est un intitulé qu'on LIT,
  // et il en portait quatre valeurs concurrentes entre 10,5 et 11,5. Les
  // capitales espacées se lisent plus bas qu'un texte courant — d'où 12 et non
  // le plancher de 12,5 du texte lu, qui ferait passer plusieurs intitulés à la
  // ligne dans les colonnes serrées.
  display: 'block', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5,
};

/** L'intitulé de rubrique, rendu. `style` n'accepte que des marges d'insertion —
 *  taille, graisse et couleur restent celles de la maison, c'est la raison
 *  d'être du composant. */
export function TitreRubrique({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ ...styleRubrique, ...style }}>{children}</div>;
}

export const inputStyle: React.CSSProperties = {
  width: '100%', height: 40, border: '1.5px solid var(--border-default)', borderRadius: 'var(--radius-md)',
  padding: '0 12px', fontSize: 13.5, color: 'var(--text-strong)', fontFamily: 'var(--font-sans)',
  // `backgroundColor` et NON le raccourci `background` : ce dernier réinitialise
  // `background-image`, donc effaçait le chevron dessiné en CSS pour les listes
  // déroulantes (globals.css). Un style en ligne l'emporte toujours sur une
  // feuille — la règle n'avait aucune chance.
  backgroundColor: '#fff',
};
export const card: React.CSSProperties = {
  background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
};

/* ---------------------------------------------------------------------------
 * Primitives d'interaction communes aux écrans qualité.
 * Elles existaient déjà en un seul exemplaire dans un écran ou un autre (busy
 * dans Retrait, Échap dans OfficeTab, infobulle dans Ressources) ; les
 * rassembler ici évite que chaque écran réinvente — ou oublie — le motif.
 * ------------------------------------------------------------------------- */

/**
 * « Suis-je encore en train de charger ? » — LA question à poser avant de
 * rendre un `0`, un « — » ou la phrase « aucun ».
 *
 * ── Le défaut qu'elle ferme (relevé d'Inès du 26/08/2026, BLOQ-03) ──────────
 *
 * Plusieurs écrans rendaient, avant l'arrivée des données, un état vide
 * **complet et crédible** — pas un squelette. `/vue-auditeur` affichait pendant
 * huit secondes « 0 doc en vigueur · 0 NC ouverte · 0 audit clôturé », puis
 * « 1 · 12 · 1 ». Rien ne signalait que c'était provisoire : un zéro se lit
 * comme une mesure, et l'écran est précisément celui qu'on montre à un
 * évaluateur externe. Un état vide n'est pas neutre — c'est une AFFIRMATION.
 *
 * ── Pourquoi les deux tests, et pas un seul ─────────────────────────────────
 *
 *  · `!booted` seul ne suffit pas : un écran monté après l'amorçage, dont la
 *    collection se recharge, retomberait sur le faux vide.
 *  · `loading` seul ne convient pas non plus : il repasse à vrai à CHAQUE
 *    rafraîchissement, y compris quand les données sont déjà à l'écran — les
 *    remplacer alors par des barres grises ferait clignoter la page après le
 *    moindre enregistrement.
 *
 * D'où la règle : on est en chargement si l'application n'a pas fini de
 * s'amorcer, OU si elle charge ET qu'on n'a encore RIEN à montrer.
 *
 * ⚠️ `tailles` prend TOUTES les collections dont l'écran tire ses chiffres.
 * N'en passer qu'une sur un écran qui en agrège quatre rendrait la garde vraie
 * trop tôt : la vue auditeur compterait ses NC alors que ses audits ne sont pas
 * arrivés, et afficherait « 0 audit clôturé » — le faux vide, en plus discret.
 */
export function enChargement(booted: boolean, loading: boolean, ...tailles: number[]): boolean {
  if (!booted) return true;
  return loading && tailles.reduce((a, b) => a + b, 0) === 0;
}

/** Lignes fantômes affichées PENDANT le chargement, à la place d'un état vide
 *  qui serait faux : tant que les données ne sont pas là, on ne peut pas
 *  affirmer « aucun résultat ». */
export function Skeleton({ rows = 4, height = 44 }: { rows?: number; height?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Chargement en cours…</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="ged-skel" style={{ height, borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px' }}>
          <span className="ged-skel-bar" style={{ width: 96, height: 10, borderRadius: 3 }} />
          <span className="ged-skel-bar" style={{ flex: 1, maxWidth: 320, height: 10, borderRadius: 3 }} />
          <span className="ged-skel-bar" style={{ width: 72, height: 10, borderRadius: 3 }} />
        </div>
      ))}
    </div>
  );
}

/** Tuiles de compteurs en attente de données — même gabarit que les vraies,
 *  pour que rien ne bouge quand les chiffres arrivent. */
export function SkeletonTiles({ n = 5 }: { n?: number }) {
  return (
    <>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ ...card, padding: '16px 18px' }}>
          <span className="ged-skel-bar" style={{ display: 'block', width: 36, height: 22, borderRadius: 4 }} />
          <span className="ged-skel-bar" style={{ display: 'block', width: '70%', height: 9, borderRadius: 3, marginTop: 10 }} />
        </div>
      ))}
    </>
  );
}

/**
 * L'ORDRE DE LA LISTE, DIT EN UNE LIGNE.
 *
 * ── Ce qu'elle ferme (relevé SYS-4 du 26/08/2026) ───────────────────────────
 *
 * « Trois sens de tri par défaut » : réclamations et audits en date croissante,
 * revues en date décroissante, non-conformités en ordre non identifiable.
 *
 * ⚠️ LES TROIS ORDRES SONT JUSTES, et les unifier serait une régression. Une
 * file d'attente se lit du plus ancien au plus récent — la réclamation qui
 * attend depuis le plus longtemps passe devant. Un programme d'audits se lit
 * dans l'ordre où il vient. Un historique de revues se lit du plus récent.
 * Trois usages, trois ordres.
 *
 * Ce qui manquait n'était pas l'uniformité : c'est que l'ordre ne se DISAIT
 * nulle part. Les tris sont composites (le statut d'abord, la date ensuite),
 * donc aucune colonne ne peut le montrer par sa flèche — d'où cette phrase.
 */
export function MentionTri({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
      Ordre&nbsp;: {children}
    </p>
  );
}

/** État vide explicite : ce qui manque, et le geste qui suit. */
export function EmptyState({ titre, indice, action }: { titre: string; indice?: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: '30px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-body)' }}>{titre}</div>
      {indice && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>{indice}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

/** Sérialise les actions longues : le bouton qui a déclenché l'appel reste
 *  désactivé jusqu'au retour, ce qui interdit le double envoi (une relance
 *  cliquée deux fois partait deux fois aux mêmes lecteurs). */
export function useBusy() {
  const [busy, setBusy] = React.useState<string | null>(null);
  const busyRef = React.useRef<string | null>(null);
  const run = React.useCallback(async (key: string, fn: () => Promise<unknown>) => {
    if (busyRef.current) return;
    busyRef.current = key; setBusy(key);
    try { await fn(); } finally { busyRef.current = null; setBusy(null); }
  }, []);
  return { busy, run, estOccupe: (k?: string) => (k ? busy === k : busy !== null) };
}

/** Échap ferme le panneau au premier plan. `actif` évite que plusieurs
 *  panneaux empilés se ferment tous d'un coup. */
/* ---------------------------------------------------------------------------
 * Liste déroulante cherchable
 *
 * Au-delà d'une poignée d'entrées, un `select` natif devient inutilisable : il
 * n'offre aucune recherche, et retrouver une personne parmi dix-neuf ou un
 * analyte parmi quatre-vingt-cinq se fait à l'œil, en faisant défiler.
 *
 * `Selecteur` prend les MÊMES enfants `<option>` qu'un select — les écrans
 * continuent d'écrire leurs options comme avant — et choisit sa forme :
 *   · jusqu'à SEUIL_RECHERCHE entrées, un vrai `<select>` natif. Il est déjà
 *     habillé (globals.css), accessible, et se pilote au clavier mieux que tout
 *     ce qu'on réécrirait ;
 *   · au-delà, un champ de recherche et une liste filtrée.
 *
 * Le seuil est une propriété du composant, pas une constante enfouie : un écran
 * qui a de bonnes raisons de vouloir la recherche plus tôt peut l'abaisser.
 *
 * ⚠️ NE PAS L'ENFERMER DANS UN CONTENEUR `overflow` (incident du 25/08/2026).
 * Au-delà du seuil, la liste est un panneau `position: absolute`. Un ancêtre en
 * `overflow: hidden` ou `overflow-x: auto` le COUPE — et la règle CSS veut
 * qu'un axe en `auto` empêche l'autre d'être `visible`, donc `overflow-x: auto`
 * suffit à masquer un panneau qui descend.
 *
 * Ce que ça donne à l'écran : le bouton s'ouvre, rien n'apparaît, aucune
 * erreur. Sur le formulaire d'ouverture d'un DVM, la RQ ne pouvait plus choisir
 * ni paramètre ni équipement ; la trace serveur montrait les options chargées
 * puis plus rien. En dessous du seuil c'est un `<select>` natif, que le
 * navigateur affiche par-dessus tout : la panne n'apparaît donc QUE sur les
 * listes longues — là où l'on ne peut pas vérifier d'un coup d'œil.
 *
 * Un conteneur qui DÉFILE (une modale `maxHeight/overflow: auto`) reste
 * utilisable : le panneau défile avec lui. C'est le conteneur qui ne défile pas
 * qui tue.
 * ------------------------------------------------------------------------- */
export const SEUIL_RECHERCHE = 8;

type OptionLue = { value: string; label: string; disabled?: boolean; groupe?: string };

/** Aplatit les enfants JSX en options exploitables. Traverse les fragments et
 *  les `<optgroup>`, et ignore les `false`/`null` que produisent les rendus
 *  conditionnels.
 *
 *  ⚠️ LES `<optgroup>` SONT TRAVERSÉS, et ce n'est pas un raffinement : sans
 *  cela, au-delà du seuil de recherche, un select groupé rendait une liste
 *  VIDE — les options existaient dans le JSX, le `<select>` natif les affichait
 *  en dessous du seuil, et elles disparaissaient au-dessus. Une panne qui ne
 *  se voit que sur les listes longues, c'est-à-dire là où l'on ne peut pas
 *  vérifier d'un coup d'œil. */
function lireOptions(children: React.ReactNode, groupe?: string): OptionLue[] {
  const out: OptionLue[] = [];
  React.Children.forEach(children, (enfant) => {
    if (!React.isValidElement(enfant)) return;
    if (enfant.type === React.Fragment) {
      out.push(...lireOptions((enfant.props as { children?: React.ReactNode }).children, groupe));
      return;
    }
    if (enfant.type === 'optgroup') {
      const g = enfant.props as { label?: string; children?: React.ReactNode };
      out.push(...lireOptions(g.children, g.label || groupe));
      return;
    }
    if (enfant.type !== 'option') return;
    const p = enfant.props as { value?: string | number; children?: React.ReactNode; disabled?: boolean };
    const label = React.Children.toArray(p.children).map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('');
    out.push({ value: String(p.value ?? label), label, disabled: p.disabled, groupe });
  });
  return out;
}

/** Insensible aux accents ET à la casse : « pean » doit trouver « Péan », sans
 *  quoi la recherche échoue sur la moitié des noms du laboratoire. */
const sansAccent = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function Selecteur({
  value, onChange, children, style, seuil = SEUIL_RECHERCHE, disabled, id, placeholder = 'Rechercher…', ...rest
}: {
  value: string;
  onChange: (valeur: string) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  seuil?: number;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const options = React.useMemo(() => lireOptions(children), [children]);
  const [ouvert, setOuvert] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [survol, setSurvol] = React.useState(0);
  const boite = React.useRef<HTMLDivElement | null>(null);

  const courante = options.find((o) => o.value === value);
  const filtrees = React.useMemo(() => {
    const qq = sansAccent(q.trim());
    return qq ? options.filter((o) => sansAccent(`${o.label} ${o.groupe || ''}`).includes(qq)) : options;
  }, [options, q]);

  // Fermeture au clic extérieur — sans elle, deux listes peuvent rester
  // ouvertes en même temps et se recouvrir.
  React.useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e: MouseEvent) => {
      if (boite.current && !boite.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);
  useEscape(ouvert, () => setOuvert(false));

  if (options.length <= seuil) {
    return (
      <select id={id} value={value} disabled={disabled} style={style}
        onChange={(e) => onChange(e.target.value)} {...rest}>
        {children}
      </select>
    );
  }

  const choisir = (v: string) => { onChange(v); setOuvert(false); setQ(''); };

  return (
    // `draggable={false}` sur le conteneur ET le bouton : un `<select>` natif
    // est exempté par le navigateur de la surface de glissement d'un ancêtre
    // `draggable`, mais un `<button>` ordinaire ne l'est pas. Sans ce drapeau,
    // le composant fonctionnait dans les vérifications automatisées (un
    // `.click()` synthétique contourne la détection de glissement) mais un
    // clic RÉEL dans l'organigramme — cases glissables — pouvait être avalé
    // par un début de glissement au lieu d'ouvrir la liste (constaté : sur la
    // case d'une personne, le clic n'ouvrait rien tant que le correctif
    // n'était pas là).
    <div ref={boite} draggable={false} style={{ position: 'relative', width: style?.width ?? '100%' }}>
      <button type="button" id={id} disabled={disabled} draggable={false}
        onClick={() => { setOuvert((o) => !o); setQ(''); setSurvol(0); }}
        aria-haspopup="listbox" aria-expanded={ouvert} aria-label={rest['aria-label']}
        style={{
          ...inputStyle, ...style, textAlign: 'start', cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8, opacity: disabled ? 0.6 : 1,
        }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: courante ? 'var(--text-strong)' : 'var(--text-muted)' }}>
          {courante?.label || '—'}
        </span>
        <I n="chevron-bas" s={14} style={{ color: 'var(--text-muted)' }} />
      </button>

      {ouvert && (
        <div role="listbox" style={{
          position: 'absolute', insetInlineStart: 0, insetInlineEnd: 0, top: 'calc(100% + 4px)', zIndex: 200,
          background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)', overflow: 'hidden',
        }}>
          <div style={{ padding: 6, borderBottom: '1px solid var(--border-subtle)' }}>
            <input autoFocus value={q} placeholder={placeholder}
              onChange={(e) => { setQ(e.target.value); setSurvol(0); }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setSurvol((s) => Math.min(s + 1, filtrees.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSurvol((s) => Math.max(s - 1, 0)); }
                else if (e.key === 'Enter') {
                  e.preventDefault();
                  const o = filtrees[survol];
                  if (o && !o.disabled) choisir(o.value);
                }
              }}
              style={{ ...inputStyle, height: 32, fontSize: 12.5 }} />
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtrees.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun résultat.</div>
            )}
            {filtrees.map((o, i) => {
              const actif = o.value === value;
              /* L'intitulé du groupe est réaffiché à chaque changement : sans
                 lui, deux options homonymes venues de groupes différents ne se
                 distinguent plus une fois la liste aplatie. */
              const enTete = o.groupe && o.groupe !== filtrees[i - 1]?.groupe ? o.groupe : null;
              return (
                <React.Fragment key={`${o.value}-${i}`}>
                {enTete && (
                  <div style={{ padding: '6px 12px 2px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>{enTete}</div>
                )}
                <button type="button" role="option" aria-selected={actif}
                  disabled={o.disabled}
                  onMouseEnter={() => setSurvol(i)}
                  onClick={() => choisir(o.value)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'start', border: 'none',
                    padding: '8px 12px', fontSize: 13, fontFamily: 'var(--font-sans)',
                    cursor: o.disabled ? 'not-allowed' : 'pointer',
                    background: i === survol ? 'var(--surface-sunken)' : 'transparent',
                    color: o.disabled ? 'var(--text-faint)' : actif ? 'var(--color-primary-active)' : 'var(--text-body)',
                    fontWeight: actif ? 700 : 400,
                  }}>
                  {o.label}
                </button>
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ padding: '5px 12px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {filtrees.length} sur {options.length}
          </div>
        </div>
      )}
    </div>
  );
}

export function useEscape(actif: boolean, onEscape: () => void) {
  React.useEffect(() => {
    if (!actif) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onEscape(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actif, onEscape]);
}

/** Glose d'un terme métier (ISO, CAPA, quarantaine…). Reprend le motif déjà
 *  utilisé pour les règles de Westgard dans Ressources. */
/**
 * UN LIEN QUI QUITTE L'APPLICATION — et qui le DIT (relevé du 27/08/2026, P0.9).
 *
 * `/intervalles-reference` rendait 315 boutons « Ouvrir la fiche » en
 * `target="_blank"`, sans le moindre signe. Trois cent quinze occasions
 * d'ouvrir un onglet sans l'avoir voulu, et de croire que l'application a
 * planté parce qu'elle ne répond plus — elle répond, dans un onglet qu'on n'a
 * pas vu s'ouvrir.
 *
 * ⚠️ Le nom accessible porte « (nouvel onglet) » : un lecteur d'écran annonce
 * le lien AVANT de l'activer, et c'est le seul moment où la mention sert.
 * `rel="noreferrer"` reste — il était déjà là, et il doit y rester.
 */
export function LienExterne({ href, children, style, className = 'ged-btn', title, nouvelOnglet = true }: {
  href: string; children: React.ReactNode; style?: React.CSSProperties;
  className?: string; title?: string;
  /** Faux = le lien remplace la page courante ; l'icône disparaît alors. */
  nouvelOnglet?: boolean;
}) {
  const mention = nouvelOnglet ? ' (nouvel onglet)' : '';
  return (
    <a href={href} className={className} style={style}
      {...(nouvelOnglet ? { target: '_blank', rel: 'noreferrer' } : {})}
      title={title ? `${title}${mention}` : undefined}
      aria-label={typeof children === 'string' ? `${children}${mention}` : undefined}>
      {children}
      {nouvelOnglet && <I n="lien-externe" s={12} style={{ opacity: 0.75 }} />}
    </a>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * REPLI JAVASCRIPT pour les zones d'écriture qui s'adaptent (28/08/2026).
 *
 * `field-sizing: content` (globals.css) fait tout le travail sur Chrome, sans
 * JavaScript ni décalage d'un rendu. Ce hook ne sert QUE là où la propriété
 * n'existe pas — d'où le `CSS.supports` en tête, qui évite de refaire à la main
 * ce que le navigateur fait déjà, et mieux.
 *
 * ── Pourquoi un écouteur DÉLÉGUÉ et non un composant ───────────────────────
 *
 * Le module compte 111 zones de texte réparties sur trente-cinq écrans. Les
 * remplacer une à une par un composant maison, c'est trente-cinq occasions
 * d'en oublier une — et la trente-sixième, écrite le mois prochain, aurait le
 * défaut d'origine. Un écouteur posé une fois sur le document couvre celles qui
 * existent ET celles qui viendront.
 *
 * ── Les trois moments où il faut mesurer ───────────────────────────────────
 *
 *  1. à la FRAPPE — l'événement `input` remonte jusqu'au document ;
 *  2. à l'APPARITION — une fenêtre qui s'ouvre monte ses zones déjà remplies,
 *     sans qu'aucune frappe n'ait lieu ; d'où l'observateur de mutations ;
 *  3. à la PRISE DE FOCUS — filet pour le cas que les deux premiers ratent :
 *     React pose la valeur d'un champ contrôlé sans déclencher `input`, et une
 *     fiche chargée après coup remplirait une zone déjà montée.
 *
 * ⚠️ La hauteur est remise à `auto` AVANT d'être lue : `scrollHeight` ne
 * DÉCROÎT jamais tant qu'une hauteur explicite est posée. Sans cette remise à
 * zéro, une zone grandit quand on écrit et ne rapetisse plus quand on efface.
 * ═══════════════════════════════════════════════════════════════════════════ */
function ajusterZone(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export function useZonesAuto() {
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content')) return undefined;

    const toutes = (racine: ParentNode) => racine.querySelectorAll<HTMLTextAreaElement>('textarea');
    const passe = (racine: ParentNode) => toutes(racine).forEach(ajusterZone);

    const surSaisie = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.tagName === 'TEXTAREA') ajusterZone(t as HTMLTextAreaElement);
    };
    document.addEventListener('input', surSaisie, true);
    document.addEventListener('focusin', surSaisie, true);

    const observateur = new MutationObserver((lots) => {
      for (const lot of lots) {
        lot.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.tagName === 'TEXTAREA') ajusterZone(n as HTMLTextAreaElement);
          else passe(n);
        });
      }
    });
    observateur.observe(document.body, { childList: true, subtree: true });
    passe(document);

    return () => {
      document.removeEventListener('input', surSaisie, true);
      document.removeEventListener('focusin', surSaisie, true);
      observateur.disconnect();
    };
  }, []);
}

export function Help({ children, aide }: { children: React.ReactNode; aide: string }) {
  return (
    // `min-height` et la marge verticale (relevé du 27/08/2026, P4b) : la
    // pastille « §7.7 » faisait 58 × 20 px et se pose sur presque toutes les
    // pages. Elle passe à 24, le minimum du WCAG 2.2. `inline-flex` plutôt
    // qu'un `padding` sur un `<span>` en ligne, dont la hauteur ne compte pas.
    <span title={aide}
      style={{
        display: 'inline-flex', alignItems: 'center', minHeight: 24,
        cursor: 'help', borderBottom: '1px dotted var(--text-muted)',
      }}>{children}</span>
  );
}

/** Référence normative glosée : « ISO 15189 §8.7 » qui dit, à l'infobulle, ce
 *  que le paragraphe exige. Sans glose, la mention décore sans informer.
 *  `prefixe` permet les écrans qui citent le paragraphe seul (« §6.6 »). */
export function RefISO({ code, prefixe = 'ISO 15189 ' }: { code: string; prefixe?: string }) {
  const texte = `${prefixe}§${code}`;
  const aide = ISO_CLAUSES[code];
  if (!aide) return <>{texte}</>;
  return <Help aide={`${texte} — ${aide}`}>{texte}</Help>;
}

/** Bouton dont l'effet est irréversible : le premier clic n'agit pas, il
 *  déplie une demande de confirmation nommant la conséquence. Pas de boîte
 *  native — elle ne peut ni être traduite ni citer l'objet correctement.
 *  Gère aussi l'état d'attente pendant l'appel. */
export function ConfirmButton({
  label, question, confirmer = 'Confirmer', icon, variant = 'primary', disabled, titre, onConfirm,
}: {
  label: string; question: string; confirmer?: string; icon?: string;
  variant?: 'primary' | 'outline' | 'danger'; disabled?: boolean; titre?: string;
  onConfirm: () => Promise<unknown> | unknown;
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(false);
  const premierRef = React.useRef<HTMLButtonElement | null>(null);
  const declencheur = React.useRef<HTMLButtonElement | null>(null);
  const boiteRef = React.useRef<HTMLSpanElement | null>(null);
  const [ancre, setAncre] = React.useState<{ x: number; y: number } | null>(null);
  useEscape(ouvert && !envoi, () => setOuvert(false));
  React.useEffect(() => { if (ouvert) premierRef.current?.focus(); }, [ouvert]);

  /* Un clic AILLEURS renonce. Sans cela, la boîte posée au portail resterait
     ouverte pendant qu'on travaille ailleurs — et sur un planning, plusieurs
     confirmations pourraient s'accumuler à l'écran sans qu'on sache laquelle
     porte sur quoi. `mousedown` et non `click` : un `click` se déclenche sur
     l'ancêtre commun du press et du release, et sélectionner du texte dans la
     question refermerait la boîte. */
  React.useEffect(() => {
    if (!ouvert || envoi || typeof document === 'undefined') return undefined;
    const dehors = (e: MouseEvent) => {
      const c = e.target as Node;
      if (boiteRef.current?.contains(c) || declencheur.current?.contains(c)) return;
      setOuvert(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert, envoi]);

  const compact = !label;

  // Bouton icône seule (label vide) : `titre` porte alors le nom accessible.
  const bouton = (
    <button ref={declencheur} disabled={disabled} title={titre} aria-label={label ? undefined : titre} className="ged-btn"
      onClick={() => {
        const r = declencheur.current?.getBoundingClientRect();
        // La position est prise AU CLIC : la case peut défiler ensuite.
        if (r) setAncre({ x: r.left, y: r.bottom + 4 });
        setOuvert(true);
      }}
      style={{ ...btnSmall(variant), opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {icon && <I n={icon} s={13} />}{label}
    </button>
  );
  if (!ouvert) return bouton;
  // Une confirmation posée sur un bouton-icône est une action de ligne —
  // supprimer un commentaire, retirer un lien. Elle se déclenche souvent et
  // s'annule sans conséquence : le bandeau ambre pleine phrase y était hors de
  // proportion, poussant la ligne et attirant l'œil plus que l'acte ne le
  // mérite. On garde alors la question courte (jusqu'au point d'interrogation)
  // et une boîte compacte ; les confirmations graves — clôture, dérogation —
  // portent un libellé et gardent la forme longue, qui explique la conséquence.
  const questionCourte = compact ? `${question.split('?')[0].trim()} ?` : question;
  const btnCompact: React.CSSProperties = compact ? { fontSize: 12.5, padding: '3px 8px' } : {};

  const boite = (
    <span ref={boiteRef} role="group" aria-label={question}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: compact ? 6 : 8, flexWrap: 'wrap',
        background: 'var(--amber-50, #FFF8EC)', border: '1px solid var(--amber-300, #EFC77B)',
        borderRadius: 'var(--radius-md)', padding: compact ? '5px 8px' : '7px 10px',
        ...(compact && ancre ? {
          /* ⚠️ AU PORTAIL quand la confirmation est COMPACTE (Inès, 29/08/2026).
             Dépliée dans le flux, elle prenait la place du bouton : dans une
             case de planning large de cent pixels, « Retirer Khawla Majbri du
             planning ? » se cassait en quatre lignes, débordait sur les cases
             voisines, et le bouton « Retirer » se retrouvait hors de la case —
             on ne voyait plus que « Annuler ».
             Posée par-dessus, elle ne déforme plus rien : la grille ne bouge
             pas d'un pixel au clic, et la question tient sur une ligne.
             `whiteSpace: nowrap` : la largeur vient du texte, plus de la case.
             `zIndex` sous le témoin de travail et le bandeau d'alerte — une
             confirmation ne doit jamais couvrir un message d'erreur. */
          position: 'fixed', left: ancre.x, top: ancre.y, zIndex: COUCHES.PLEIN_ECRAN,
          whiteSpace: 'nowrap', boxShadow: 'var(--shadow-md)',
        } : null),
      }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{questionCourte}</span>
      <button ref={premierRef} disabled={envoi} className="ged-btn" style={{ ...btnSmall(variant === 'outline' ? 'primary' : variant), ...btnCompact, opacity: envoi ? 0.6 : 1 }}
        onClick={async () => { setEnvoi(true); try { await onConfirm(); } finally { setEnvoi(false); setOuvert(false); } }}>
        {envoi ? 'En cours…' : confirmer}
      </button>
      <button onClick={() => setOuvert(false)} disabled={envoi} className="ged-btn" style={{ ...btnSmall('outline'), ...btnCompact }}>Annuler</button>
    </span>
  );

  /* Compacte : le bouton RESTE en place et la boîte se pose au-dessus. Le
     remplacer aurait fait sauter la grille au clic — et sur un planning de
     94 cases, ce saut déplace la case voisine sous le curseur. */
  if (compact && ancre && typeof document !== 'undefined') {
    return <>{bouton}{createPortal(boite, document.body)}</>;
  }
  return boite;
}

/* ---------------------------------------------------------------------------
 * Modale commune (socle UX du 10/08/2026).
 *
 * Seize overlays réécrits à la main coexistaient dans le module, avec cinq
 * fonds, huit `zIndex` et des régimes de défilement contradictoires ; la
 * moitié ignorait Échap. Cette modale fixe le contrat UNIQUE :
 *   · Échap ET clic sur le fond ferment ;
 *   · hauteur bornée, CORPS seul défilant — le titre et le pied restent
 *     visibles quelle que soit la longueur du formulaire ;
 *   · pied normé : « Annuler » PUIS l'action, alignés à droite (décision
 *     utilisateur du 10/08/2026 — l'ordre du module RH, généralisé).
 * Les écrans existants migrent dessus au fil des lots ; tout nouvel overlay
 * DOIT l'utiliser.
 * ------------------------------------------------------------------------- */
/**
 * LE VOILE d'un overlay — une seule teinte pour toute la suite (24/08/2026).
 *
 * Sept nuances cohabitaient : `rgba(11,20,38,.45)` (celle-ci), `(11,20,38,.35)`,
 * `(15,23,42,.45)`, `(15,23,42,.32)`, `(14,39,64,.45)`, `(14,39,64,.42)` et
 * `(10,25,41,.45)`. Aucune n'était un choix : chacune est née d'une boîte
 * recopiée d'un écran voisin. Ouvrir deux fenêtres de suite montrait deux fonds
 * différents pour le même geste.
 *
 * ⚠️ Les `zIndex` des overlays ne sont PAS tous normalisés : ils décrivent des
 * empilements réels (une boîte par-dessus une boîte) qu'on ne peut pas
 * réordonner sans voir l'écran. Le composant `Modale` est à 400.
 */
export const VOILE = 'rgba(11,20,38,0.45)';

/* ═══════════════════════════════════════════════════════════════════════════
 * LES COUCHES — et pourquoi seules les deux dernières sont normées (25/08/2026)
 *
 * ── Le défaut, tel qu'il s'est présenté ────────────────────────────────────
 *
 * « Quand il y a une fenêtre, le message d'alerte apparaît en dessous et n'est
 * pas lisible. Je l'ai eu avec : Connexion interrompue. Votre saisie a
 * peut-être été enregistrée : vérifiez le registre avant de refaire
 * l'opération. »
 *
 * C'est le pire cas possible. Ce message-là dit qu'une écriture a peut-être
 * abouti — le relire est la seule chose qui empêche de refaire l'opération et
 * de créer un doublon. Il était rendu à `zIndex: 120`, sous les vingt-quatre
 * empilements du module : toute fenêtre modale (400), le plein écran
 * bureautique (900), l'affichage de la norme (950). Le témoin « l'application
 * travaille » (150) disparaissait de la même façon : dans une fenêtre, plus
 * rien ne disait qu'un clic avait été pris.
 *
 * ── Ce qu'on norme, et ce qu'on laisse ─────────────────────────────────────
 *
 * On NE renumérote PAS les vingt-quatre overlays : leur ordre décrit des
 * empilements réels qu'on ne peut pas rejuger sans voir l'écran, et les
 * déplacer en bloc casserait des superpositions qui marchent.
 *
 * On norme les deux couches qui n'appartiennent à AUCUN écran et doivent
 * survivre à tout ce qu'on empile sous elles :
 *
 *   · `MESSAGE` — le bandeau d'alerte. Il est le plus haut, sans exception. Un
 *     message qu'on ne peut pas lire ne vaut pas mieux qu'un message qu'on n'a
 *     pas écrit ;
 *   · `TRAVAIL` — le témoin d'écriture en cours. Juste EN DESSOUS du message :
 *     les deux se placent au même endroit de l'écran, et un témoin par-dessus
 *     un message masquerait précisément ce qu'on veut faire lire. Le témoin
 *     s'efface de toute façon quand la réponse arrive.
 *
 * ⚠️ Tout nouvel overlay reste sous `PLEIN_ECRAN`. Aucun ne doit jamais
 * dépasser `TRAVAIL` : ce serait rouvrir le défaut d'aujourd'hui.
 * ═══════════════════════════════════════════════════════════════════════════ */
export const COUCHES = {
  /* ── Trois couches ajoutées le 27/08/2026 (relevé MAJ-13) ─────────────────
     Elles existaient déjà en chiffres nus, dispersés dans une vingtaine de
     fichiers — et deux d'entre eux se contredisaient : la fiche NC s'ouvrait en
     modale à `150`, tandis que la commande flottante « Déclarer une NC » vivait
     à `300`. Le bouton restait donc OPAQUE ET CLIQUABLE par-dessus le voile de
     la fenêtre qu'il venait d'ouvrir.

     ⚠️ Ce n'est pas qu'un désagrément visuel : cliquer un bouton qui affleure
     au-dessus d'une modale ouvre une SECONDE saisie par-dessus la première, et
     l'on ne sait plus laquelle on remplit.

     L'ordre est celui de l'intention : ce qu'on regarde couvre ce qu'on
     pourrait vouloir ouvrir. */
  /** Commandes flottantes (gommette, « Une idée ? »). Sous toute fenêtre. */
  FLOTTANT: 300,
  /** Voile d'une fenêtre modale. Couvre les commandes flottantes. */
  MODALE: 400,
  /** Contenu d'une modale, au-dessus de son propre voile. */
  MODALE_CONTENU: 420,
  /* ⚠️ AJOUTÉE LE 30/08/2026, sur un défaut rapporté par la RQ : « quand je
     clique sur le mesurande à partir du dossier d'un examen, la fenêtre s'ouvre
     derrière la fenêtre existante, donc je ne la vois pas. »

     Le panneau de fiche liée était rendu à `zIndex: 300` en dur — un chiffre
     posé le 12/08, avant que cette échelle existe, et jamais normalisé (l'ordre
     de cette table le disait déjà : « les zIndex des overlays ne sont PAS tous
     normalisés »). 300, c'est `FLOTTANT` : SOUS toute fenêtre modale.

     Or le panneau s'ouvre presque toujours DEPUIS quelque chose — une modale,
     un dossier — et ce qu'on vient d'ouvrir doit couvrir ce d'où l'on vient.
     Il lui faut donc sa propre couche, au-dessus des modales. */
  /** Panneau de fiche liée. Il s'ouvre DEPUIS une modale : il la couvre. */
  PANNEAU: 500,
  /** Le plus haut empilement d'ÉCRAN existant (affichage de la norme). */
  PLEIN_ECRAN: 950,
  /** Le témoin « l'application travaille ». Au-dessus de tout écran. */
  TRAVAIL: 1000,
  /** Le bandeau d'alerte. Au-dessus de TOUT, sans exception. */
  MESSAGE: 1010,
} as const;

/* ---------------------------------------------------------------------------
 * BARRE D'ACTIONS — l'action ne se cherche pas (U2, 25/08/2026)
 *
 * « Revois tous les boutons d'actions, il ne faut pas qu'ils soient touuuuut en
 * bas d'un tableau. C'est peu lisible. »
 *
 * Le défaut n'est pas la position en soi : c'est qu'il faut FAIRE DÉFILER pour
 * atteindre l'action, et donc la chercher. Sur un écran de réglages qui tient
 * en trois écrans de haut, on remplit un champ tout en haut et l'on ne sait
 * plus si un bouton d'enregistrement existe.
 *
 * La barre reste COLLÉE au bas de la zone de défilement : l'action est visible
 * quel que soit l'endroit où l'on se trouve, sans changer la place à laquelle
 * on l'attend. Même convention que le pied de `Modale` — « Annuler » d'abord,
 * l'action ensuite, alignés à droite.
 *
 * ⚠️ Elle n'est PAS `position: fixed`. Fixée à la fenêtre, elle recouvrirait le
 * bas de tous les autres écrans, y compris ceux qui n'ont aucune action ; et
 * sur mobile elle mangerait le peu de hauteur disponible. `sticky` la garde
 * dans le flux : elle occupe sa place, et se colle seulement quand le contenu
 * défile sous elle.
 * ------------------------------------------------------------------------- */
export function BarreActions({ children, aide }: {
  children: React.ReactNode;
  /** Ce que l'action va faire, dit à côté d'elle plutôt qu'au-dessus du
   *  tableau : c'est ici qu'on décide, c'est ici qu'il faut le lire. */
  aide?: React.ReactNode;
}) {
  return (
    <div style={{
      position: 'sticky', bottom: 0, zIndex: 2,
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      marginTop: 18, padding: '12px 0',
      background: 'var(--surface-page)',
      borderTop: '1px solid var(--border-default)',
    }}>
      {aide && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, flex: '1 1 240px' }}>
          {aide}
        </span>
      )}
      <span style={{ flex: 1 }} />
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LE PIÈGE DE FOCUS D'UNE FENÊTRE MODALE (relevé du 27/08/2026, P0.6).
 *
 * ── Ce qui a été mesuré ────────────────────────────────────────────────────
 *
 * À l'ouverture d'un `[role=dialog]`, `document.activeElement` restait `BODY`.
 * Trois conséquences, dans l'ordre où on les rencontre :
 *
 *   · la première tabulation partait dans la barre latérale, DERRIÈRE la
 *     fenêtre — on tabulait à l'aveugle dans une page qu'on ne voit plus ;
 *   · un lecteur d'écran continuait d'annoncer le contenu de dessous, comme si
 *     la fenêtre n'existait pas ;
 *   · fermer la fenêtre rendait le focus au néant, et il fallait repartir du
 *     haut de la page pour retrouver la ligne qu'on venait d'ouvrir.
 *
 * ── Ce que le hook fait, et dans quel ordre ────────────────────────────────
 *
 *  1. il MÉMORISE l'élément qui avait le focus — c'est presque toujours la
 *     ligne ou le bouton qui a ouvert la fenêtre ;
 *  2. il donne le focus au premier élément utile de la fenêtre ;
 *  3. il fait BOUCLER la tabulation à l'intérieur ;
 *  4. il REND le focus à l'élément mémorisé à la fermeture.
 *
 * ⚠️ Le point 4 est celui qu'on oublie et c'est le plus utile : sans lui, on
 * ferme une fiche et l'on ne sait plus d'où l'on venait.
 *
 * ⚠️ La liste des éléments focalisables est relue À CHAQUE TABULATION et non
 * mémorisée à l'ouverture : le contenu d'une fenêtre change (un bloc qui se
 * déplie, une liste qui arrive du serveur), et une liste figée piégerait le
 * focus sur des éléments disparus.
 * ═══════════════════════════════════════════════════════════════════════════ */
const FOCALISABLES = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export function usePiegeFocus(boite: React.RefObject<HTMLElement | null>, actif = true) {
  React.useEffect(() => {
    if (!actif || typeof document === 'undefined') return undefined;
    const precedent = document.activeElement as HTMLElement | null;
    const el = boite.current;
    if (!el) return undefined;

    /* Le premier élément utile, et non le tout premier : la croix « Fermer »
       ouvre la fenêtre sur son geste de sortie, ce qui se lit comme une
       invitation à partir. On préfère le premier champ ou bouton du CORPS ;
       à défaut, la boîte elle-même, qui porte `aria-label`. */
    const cibles = () => Array.from(el.querySelectorAll<HTMLElement>(FOCALISABLES))
      .filter((x) => x.offsetParent !== null || x === document.activeElement);
    const liste = cibles();
    const premier = liste.find((x) => x.getAttribute('aria-label') !== 'Fermer') || liste[0] || el;
    premier.focus({ preventScroll: true });

    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const l = cibles();
      if (!l.length) { e.preventDefault(); return; }
      const debut = l[0];
      const fin = l[l.length - 1];
      const a = document.activeElement;
      // La boucle : au-delà du dernier on revient au premier, et l'inverse
      // avec Maj. Sans elle, la tabulation sort par le bas dans la page.
      if (!e.shiftKey && a === fin) { e.preventDefault(); debut.focus(); }
      else if (e.shiftKey && a === debut) { e.preventDefault(); fin.focus(); }
      else if (!el.contains(a)) { e.preventDefault(); debut.focus(); }
    };
    document.addEventListener('keydown', surTouche, true);
    return () => {
      document.removeEventListener('keydown', surTouche, true);
      // `isConnected` : rendre le focus à un élément que le rendu a retiré du
      // document ne fait rien — et laisse le focus sur `BODY`, c'est-à-dire le
      // défaut qu'on corrige. On retombe alors sur le corps de la page.
      if (precedent && precedent.isConnected) precedent.focus({ preventScroll: true });
    };
  }, [actif, boite]);
}

/* La règle « on ne quitte une fenêtre que par la croix » (04/09/2026), ses
   trois décisions et les deux pièges qu'elle évite sont écrits UNE SEULE FOIS,
   dans `shared/ui/fenetreModale.tsx`. Ce composant n'en est qu'un appelant. */

export function Modale({
  titre, sousTitre, onClose, children, pied, largeur = 560,
  protegee = false, onSortieBloquee, sansSaisie = false,
}: {
  titre: string; sousTitre?: React.ReactNode; onClose: () => void;
  children: React.ReactNode;
  /** Boutons du pied — composer « Annuler » d'abord, l'action ensuite. */
  pied?: React.ReactNode; largeur?: number;
  /**
   * GARDE DÉCLARÉE PAR L'ÉCRAN, au-dessus de la détection automatique.
   *
   * La règle générale (voir le pavé ci-dessus) suffit dans la quasi-totalité
   * des cas. `protegee` reste pour la minorité où l'écran sait quelque chose
   * que le DOM ne dit pas — une saisie portée par un composant qui n'émet pas
   * d'événement de champ, un envoi en cours qu'il ne faut pas interrompre.
   * Quand elle vaut vrai, la croix et Échap appellent `onSortieBloquee` et
   * c'est l'écran qui pose la question.
   */
  protegee?: boolean;
  onSortieBloquee?: () => void;
  /**
   * FENÊTRE DE CONSULTATION : la croix ferme sans rien demander.
   *
   * À poser sur ce qui MONTRE et n'enregistre pas — un journal, un aperçu, une
   * fiche liée. Sans elle, un simple `<select>` de filtre suffirait à déclencher
   * l'alerte, et une alerte qui se trompe est une alerte qu'on cesse de lire.
   * ⚠️ Elle ne rend PAS le clic à côté actif : la sortie reste la croix.
   */
  sansSaisie?: boolean;
}) {
  const boite = React.useRef<HTMLDivElement | null>(null);
  /* L'alerte est rendue DANS la boîte et non en fenêtre imbriquée : une modale
     par-dessus une modale se disputerait le piège du focus avec la première, et
     la question porte de toute façon sur cette fenêtre-ci. */
  const sortie = useSortieFenetre({ boite, onClose, sansSaisie });
  /* `protegee` passe DEVANT la détection : quand l'écran sait quelque chose que
     le DOM ne dit pas, c'est lui qui pose la question. */
  const sortir = React.useCallback(() => {
    if (protegee) { onSortieBloquee?.(); return; }
    sortie.demanderSortie();
  }, [protegee, onSortieBloquee, sortie]);
  /* Le focus entre dans la fenêtre, y tourne, et revient d'où il venait à la
     fermeture. Voir `usePiegeFocus`. */
  usePiegeFocus(boite);
  /* ⚠️ ET LA PAGE DE DESSOUS NE DÉFILE PLUS (relevé du 27/08/2026, P0.6) :
     `scrollBy(0, 400)` fonctionnait fenêtre ouverte, si bien qu'un geste de
     molette mal placé emportait la page derrière la fenêtre et l'on retrouvait
     un contexte différent en fermant. Le verrou est celui du socle partagé —
     il fige `<body>` en mémorisant la position plutôt que de poser un simple
     `overflow: hidden`, que Safari iOS ignore au toucher. */
  useVerrouScroll(true);
  return (
    /* ⚠️ AUCUN gestionnaire de fermeture sur le voile — c'est la règle du
       04/09/2026, et le contrôle statique `qualite-fenetre-sortie-par-la-croix`
       échoue si on en repose un. Le `onMouseDown` ne sert qu'à faire tressaillir
       la boîte, pour que l'inertie se lise comme « la sortie est là ». */
    <div {...sortie.proprietesVoile} style={{
      position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div ref={boite} role="dialog" aria-modal="true" aria-label={titre}
        {...sortie.proprietesBoite}
        style={{
          ...card, width: `min(${largeur}px, 100%)`, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          /* L'alerte de sortie se pose en `absolute` DANS cette boîte. */
          position: 'relative',
          ...sortie.proprietesBoite.style,
        }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--border-default)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</div>
            {sousTitre && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{sousTitre}</div>}
          </div>
          <button onClick={sortir} aria-label="Fermer" className="ged-btn"
            style={{
              background: sortie.refuse ? 'var(--color-primary-soft)' : 'none', border: 'none', cursor: 'pointer',
              color: sortie.refuse ? 'var(--color-primary-active)' : 'var(--text-muted)', padding: 4, borderRadius: 6,
              transition: 'background 140ms, color 140ms',
            }}>
            <I n="x" s={16} />
          </button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>{children}</div>
        {pied && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '13px 18px', borderTop: '1px solid var(--border-default)', background: 'var(--surface-sunken)' }}>
            {pied}
          </div>
        )}
        {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}
      </div>
    </div>
  );
}

/** En-tête de page COMMUN — l'audit du 10/08/2026 comptait trois styles de
 *  titre concurrents (sans 26/700, serif display, artisanaux). Un seul style
 *  désormais, et le slot `actions` place les boutons d'écran toujours au même
 *  endroit : en haut à droite, alignés sur la ligne de base du titre. */
/**
 * ⚠️ PRIMITIVE DE MISE EN FORME, PAS DE COMPOSITION.
 *
 * Elle place un chapeau, un titre, un sous-titre et des actions — rien de plus.
 * La RÈGLE de composition du chapeau (« RUBRIQUE · §X.Y ») vit dans
 * `components/EnteteEcran.tsx`, qui appelle celle-ci. C'est ce découpage qui a
 * permis de fermer SYS-1 sans créer de cycle d'import : `EnteteEcran` a besoin
 * de `<Norme>`, et `Norme.tsx` importe déjà `ui.tsx`.
 *
 * Les écrans neufs passent par `EnteteEcran`. `eyebrow` reste accepté pour les
 * cinquante-cinq écrans non encore repris — `qualite-entetes.test.js` empêche
 * leur nombre d'augmenter.
 */
export function TitrePage({ eyebrow, titre, sousTitre, actions }: {
  eyebrow?: React.ReactNode; titre: React.ReactNode;
  sousTitre?: React.ReactNode; actions?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-strong)', margin: '10px 0 0' }}>{titre}</h1>
        {sousTitre && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '8px 0 0', maxWidth: '70ch' }}>{sousTitre}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

/**
 * REMONTER EN HAUT quand un sous-onglet remplace le contenu de l'écran.
 *
 * ── Le défaut qu'il corrige (constaté sur les NC, 24/08/2026) ───────────────
 *
 * « Changer d'écran remet en haut » n'existait que pour les écrans (`view`,
 * dans le Shell) — pas pour les sous-onglets, qui sont de l'état local. Or
 * basculer « Registre & traitement » → « Analyse fine » depuis une page
 * défilée RACCOURCIT la page : le navigateur borne le défilement, la barre
 * latérale collante décroche de son `top` et SAUTE verticalement. Le défaut
 * n'était pas propre aux NC : tout écran à sous-onglets de hauteurs
 * différentes l'avait.
 *
 * ── La règle ────────────────────────────────────────────────────────────────
 *
 * Un sous-onglet qui REMPLACE le contenu est une arrivée : on remonte,
 * exactement comme en changeant d'écran. Un FILTRE ou un MODE qui re-rend la
 * même liste n'en est pas une : on ne touche pas au défilement — remonter
 * ferait perdre sa ligne au lecteur.
 *
 * Usage : `useRemonteEnHaut(onglet)` dans le composant qui porte l'état.
 * Écran aussi montable EN DOSSIER (Calibrations, Maintenances…) : passer
 * `useRemonteEnHaut(enDossier ? null : onglet)` — la clé constante ne
 * déclenche jamais, le dossier garde son propre défilement.
 */
export function useRemonteEnHaut(cle: unknown) {
  const premier = React.useRef(true);
  React.useEffect(() => {
    if (premier.current) { premier.current = false; return; }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [cle]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Confirmation impérative — le remplaçant de `window.confirm`, qui bloquait
 *  le fil du navigateur, ignorait Échap-sur-fond et jurait avec le reste de
 *  l'interface. Usage :
 *    const { demander, confirmation } = useConfirmation();
 *    …render… {confirmation}
 *    if (!(await demander('Des modifications non enregistrées seront perdues. Fermer ?'))) return;
 *  La question DOIT nommer la conséquence, pas seulement demander « Êtes-vous
 *  sûr ? ». Fermer par Échap, le fond ou « Annuler » répond non. */
/**
 * MENU D'ACTIONS — un seul bouton pour ce qui ÉCRIT (Lot 3, 02/09/2026).
 *
 * ── Pourquoi il existe ─────────────────────────────────────────────────────
 *
 * La barre du planning alignait douze boutons et trois listes sur trois rangs,
 * sans hiérarchie : « Postes en colonnes », qui ne change QUE ce que je vois,
 * y voisinait avec « Recopier la semaine précédente », qui écrit quatre-vingt-dix
 * affectations. Rien ne les distinguait, ni la forme ni la place.
 *
 * La règle que ce composant sert : **un bouton qui change ce que je vois et un
 * bouton qui change les données ne doivent jamais se ressembler ni se côtoyer.**
 * Les bascules d'affichage restent à gauche, en clair ; tout ce qui écrit passe
 * par ici, derrière un geste de plus.
 *
 * ⚠️ CE GESTE DE PLUS EST LE POINT, pas un défaut d'ergonomie. Une action de
 * masse atteignable au premier clic se déclenche par erreur — et « recopier la
 * semaine précédente » ne se défait pas d'un bouton.
 *
 * ⚠️ Les entrées DESTRUCTRICES portent `danger` : elles sont rouges et séparées.
 * Une couleur ne suffit pas à elle seule (personne ne la voit toujours), d'où
 * aussi un libellé au verbe et une confirmation derrière.
 */
export function MenuActions({ libelle = 'Actions', entrees, disabled }: {
  libelle?: string;
  entrees: {
    cle: string; label: string; icon?: string; aide?: string;
    danger?: boolean; desactive?: boolean; motifDesactive?: string;
    onChoisir: () => void;
  }[];
  disabled?: boolean;
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const boite = React.useRef<HTMLDivElement | null>(null);
  const declencheur = React.useRef<HTMLButtonElement | null>(null);
  useEscape(ouvert, () => { setOuvert(false); declencheur.current?.focus(); });

  /* Un clic AILLEURS referme. `mousedown` et non `click` : sélectionner du
     texte dans une aide en glissant hors du menu le refermerait autrement. */
  React.useEffect(() => {
    if (!ouvert || typeof document === 'undefined') return undefined;
    const dehors = (e: MouseEvent) => {
      const c = e.target as Node;
      if (boite.current?.contains(c) || declencheur.current?.contains(c)) return;
      setOuvert(false);
    };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, [ouvert]);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} className="no-print">
      <button ref={declencheur} type="button" className="ged-btn" disabled={disabled}
        aria-haspopup="menu" aria-expanded={ouvert}
        onClick={() => setOuvert((o) => !o)}
        style={{ ...btnSmall(ouvert ? 'primary' : 'outline'), opacity: disabled ? 0.5 : 1 }}>
        {libelle} <span aria-hidden style={{ fontSize: 10 }}>▾</span>
      </button>
      {ouvert && (
        <div ref={boite} role="menu" aria-label={libelle}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', insetInlineEnd: 0, zIndex: 60,
            minWidth: 268, ...card, padding: 5, boxShadow: '0 8px 28px rgba(15,23,42,0.16)',
          }}>
          {entrees.map((e) => (
            <button key={e.cle} type="button" role="menuitem" className="ged-btn"
              disabled={e.desactive}
              title={e.desactive ? e.motifDesactive : e.aide}
              onClick={() => { setOuvert(false); e.onChoisir(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
                background: 'none', border: 'none', padding: '7px 9px', borderRadius: 'var(--radius-sm)',
                font: 'inherit', fontSize: 12.5, cursor: e.desactive ? 'not-allowed' : 'pointer',
                opacity: e.desactive ? 0.45 : 1,
                color: e.danger ? 'var(--red-600)' : 'var(--text-strong)',
              }}>
              {e.icon && <I n={e.icon} s={13} />}
              <span style={{ flex: 1 }}>{e.label}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * POIGNÉE DE REDIMENSIONNEMENT d'une colonne (Lot 4, 02/09/2026).
 *
 * ── Trois gestes, et le troisième est celui qu'on oublie ───────────────────
 *
 *  · GLISSER — le geste attendu ;
 *  · DOUBLE-CLIC — ajustement au contenu le plus large ;
 *  · FLÈCHES GAUCHE / DROITE au clavier. C'est celui qu'on n'écrit jamais, et
 *    c'est celui sans lequel la fonction n'existe pas pour qui ne tient pas de
 *    souris. La poignée est donc un `separator` focalisable, avec ses valeurs
 *    `aria-value*` — un lecteur d'écran annonce alors la largeur courante.
 *
 * ⚠️ ON ÉCOUTE SUR `window`, PAS SUR LA POIGNÉE. Un glissement rapide sort du
 * curseur de la poignée — deux pixels de large — et l'événement se perd : la
 * colonne cesse de suivre en cours de geste, sans que rien ne l'explique. La
 * capture de pointeur règle cela une fois pour toutes.
 *
 * ⚠️ AUCUN ÉTAT INTERMÉDIAIRE ICI. La largeur vit chez l'appelant : deux
 * sources — une locale pendant le glissé, une remontée à la fin — divergeraient
 * au premier geste interrompu.
 */
export function PoigneeColonne({ largeur, onLargeur, onAjuster, label, min = 96, max = 420 }: {
  largeur: number;
  onLargeur: (px: number) => void;
  /** Double-clic : ajuster au contenu. Absent = le double-clic ne fait rien. */
  onAjuster?: () => void;
  label: string;
  min?: number; max?: number;
}) {
  const depart = React.useRef<{ x: number; l: number } | null>(null);
  const borner = (px: number) => Math.max(min, Math.min(max, Math.round(px)));

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Largeur de la colonne ${label}`}
      aria-valuenow={largeur}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      className="no-print"
      title={`Glisser pour redimensionner${onAjuster ? ' · double-clic pour ajuster au contenu' : ''} · flèches au clavier`}
      onPointerDown={(e) => {
        e.preventDefault(); e.stopPropagation();
        depart.current = { x: e.clientX, l: largeur };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!depart.current) return;
        /* `dir` : en écriture de droite à gauche, tirer vers la gauche ÉLARGIT.
           Le signe se lit sur la direction du document, jamais supposé. */
        const rtl = typeof document !== 'undefined' && document.dir === 'rtl';
        const delta = (e.clientX - depart.current.x) * (rtl ? -1 : 1);
        onLargeur(borner(depart.current.l + delta));
      }}
      onPointerUp={(e) => {
        depart.current = null;
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      }}
      onDoubleClick={onAjuster}
      onKeyDown={(e) => {
        /* Douze pixels par appui, cinquante avec Maj : assez pour traverser une
           colonne en quelques touches sans rendre le réglage fin impossible. */
        const pas = e.shiftKey ? 50 : 12;
        if (e.key === 'ArrowRight') { e.preventDefault(); onLargeur(borner(largeur + pas)); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); onLargeur(borner(largeur - pas)); }
        else if (e.key === 'Home' && onAjuster) { e.preventDefault(); onAjuster(); }
      }}
      style={{
        position: 'absolute', insetBlock: 0, insetInlineEnd: -3, width: 7,
        cursor: 'col-resize', touchAction: 'none',
        /* Discrète au repos, visible à l'approche : une poignée toujours
           dessinée ajouterait sept traits verticaux à une grille qui en a déjà
           assez. Au focus clavier elle se montre franchement — sans quoi on ne
           saurait pas où l'on est. */
        background: 'transparent',
      }}
      onFocus={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; }}
      onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border-strong)'; }}
      onMouseLeave={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.background = 'transparent'; }}
    />
  );
}

export function useConfirmation() {
  const [etat, setEtat] = React.useState<{
    question: string; titre: string; confirmer: string; danger: boolean; resoudre: (ok: boolean) => void;
  } | null>(null);
  const demander = React.useCallback(
    /* `danger` : la confirmation d'une SUPPRESSION IRRÉVERSIBLE (UI-CONVENTIONS §4). Le bouton est rouge
       et ne prend PAS le focus d'office : un Entrée distrait ne doit pas détruire une donnée. */
    (question: string, opts?: { titre?: string; confirmer?: string; danger?: boolean }) =>
      new Promise<boolean>((resoudre) => setEtat({
        question, titre: opts?.titre || 'Confirmer', confirmer: opts?.confirmer || 'Confirmer', danger: opts?.danger === true, resoudre,
      })),
    [],
  );
  const repondre = (ok: boolean) => { setEtat((e) => { e?.resoudre(ok); return null; }); };

  /* ── Saisie d'un TEXTE (motif, nom, date) — le remplaçant de `window.prompt` ─────────────────────
     `min` : nombre de caractères exigés (le bouton reste grisé en dessous, et le compteur le dit).
     Rend le texte saisi (rogné), ou `null` si l'on annule. Même fenêtre que `demander`, donc même
     focus, même Échap, même garde de sortie. */
  const [saisie, setSaisie] = React.useState<{
    question: string; titre: string; confirmer: string; danger: boolean; placeholder: string;
    min: number; multiligne: boolean; type: 'text' | 'date'; resoudre: (v: string | null) => void;
  } | null>(null);
  const [texte, setTexte] = React.useState('');
  const demanderTexte = React.useCallback(
    (question: string, opts?: {
      titre?: string; confirmer?: string; danger?: boolean; placeholder?: string; min?: number;
      multiligne?: boolean; type?: 'text' | 'date'; valeurInitiale?: string;
    }) => new Promise<string | null>((resoudre) => {
      setTexte(opts?.valeurInitiale || '');
      setSaisie({
        question, titre: opts?.titre || 'Saisie', confirmer: opts?.confirmer || 'Enregistrer', danger: opts?.danger === true,
        placeholder: opts?.placeholder || '', min: opts?.min ?? 1, multiligne: opts?.multiligne !== false && opts?.type !== 'date',
        type: opts?.type || 'text', resoudre,
      });
    }),
    [],
  );
  const repondreTexte = (valeur: string | null) => { setSaisie((e) => { e?.resoudre(valeur); return null; }); };
  const saisieOuverte = saisie ? (
    <Modale titre={saisie.titre} onClose={() => repondreTexte(null)} largeur={480}
      pied={(
        <PiedActions
          taille="sm"
          annuler={{ onClick: () => repondreTexte(null) }}
          principale={{
            libelle: saisie.confirmer, variante: saisie.danger ? 'danger' : 'primaire',
            disabled: texte.trim().length < saisie.min, onClick: () => repondreTexte(texte.trim()),
          }}
        />
      )}>
      <label style={{ display: 'grid', gap: 8, fontSize: 13, color: 'var(--text-strong)', lineHeight: 1.55 }}>
        <span style={{ whiteSpace: 'pre-line' }}>{saisie.question}</span>
        {saisie.multiligne ? (
          <textarea autoFocus rows={3} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder={saisie.placeholder}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
        ) : (
          <input autoFocus type={saisie.type} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder={saisie.placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter' && texte.trim().length >= saisie.min) repondreTexte(texte.trim()); }}
            style={inputStyle} />
        )}
        {saisie.min > 1 && (
          <span style={{ fontSize: 12, color: texte.trim().length < saisie.min ? 'var(--red-600)' : 'var(--text-muted)' }}>
            {texte.trim().length}/{saisie.min} caractères minimum.
          </span>
        )}
      </label>
    </Modale>
  ) : null;

  const confirmation = etat || saisie ? (<>{saisieOuverte}{etat ? (
    <Modale titre={etat.titre} onClose={() => repondre(false)} largeur={440}
      pied={<>
        <Bouton taille="sm" onClick={() => repondre(false)}>Annuler</Bouton>
        <Bouton variante={etat.danger ? 'danger' : 'primaire'} taille="sm" autoFocus={!etat.danger} onClick={() => repondre(true)}>{etat.confirmer}</Bouton>
      </>}>
      <p style={{ fontSize: 13, color: 'var(--text-strong)', margin: 0, lineHeight: 1.55, whiteSpace: 'pre-line' }}>{etat.question}</p>
    </Modale>
  ) : null}</>) : null;
  return { demander, demanderTexte, confirmation };
}

/* `SortableTh` A ÉTÉ SUPPRIMÉ le 10/10/2026 (L4, dette de tri). Il posait
   `aria-sort` sur un `<button>` — inerte — et faisait un TROISIÈME moteur de
   tri à côté du tableau et du tri maison des présences. Un tableau se trie par
   `<Tableau>` (shared/ui/Tableau.tsx) ; une liste de cartes par `useTri` +
   `BoutonsTri` (shared/ui/Tri.tsx), qui partagent le même moteur. */

/** Barre commune aux registres qualité : recherche + jeux de pastilles de
 *  filtre + compteur « x sur y » + remise à zéro. Les registres n'avaient
 *  aucun de ces contrôles, seul le catalogue documentaire en avait. */

/** Champ de recherche avec sa croix d'effacement.
 *
 *  ── Pourquoi une brique et pas un `type="search"` ───────────────────────────
 *
 *  Le natif offre bien une croix, mais WebKit ne l'affiche qu'au survol et
 *  Firefox pas du tout : la commande existerait sur un navigateur et pas sur
 *  l'autre, ce qui est pire que son absence. Elle est donc dessinée.
 *
 *  ── Trois détails qui décident si elle sert vraiment ────────────────────────
 *
 *  · Elle n'apparaît QUE s'il y a quelque chose à effacer — une croix inerte
 *    sur un champ vide se clique et ne fait rien, ce qui use la confiance.
 *  · Le focus REVIENT dans le champ après l'effacement : on efface pour
 *    retaper, pas pour contempler une liste complète.
 *  · Échap efface aussi, sans quitter le champ — le geste attendu au clavier.
 *
 *  La réserve de place à droite (`padding-inline-end`) est permanente : la
 *  poser seulement quand la croix est là ferait sauter le texte d'un caractère
 *  à la première frappe.
 */
export function ChampRecherche({ valeur, onChange, placeholder, style, autoFocus }: {
  valeur: string;
  onChange: (v: string) => void;
  placeholder: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
}) {
  const champ = React.useRef<HTMLInputElement | null>(null);
  const rempli = valeur.length > 0;
  const effacer = () => { onChange(''); champ.current?.focus(); };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flex: style?.flex ?? '1 1 260px', maxWidth: style?.maxWidth ?? 420 }}>
      <input
        ref={champ}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && rempli) { e.preventDefault(); e.stopPropagation(); effacer(); } }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        /* ⚠️ `text-overflow: ellipsis` sur le PLACEHOLDER (relevé SYS-11 du
           26/08/2026 : « Rechercher un paramètr »). Le texte était complet ; le
           champ, posé dans une colonne étroite, le coupait NET au milieu d'un
           mot. Une coupure franche se lit comme un défaut d'affichage — on
           soupçonne le texte, pas la largeur. Les points de suspension disent
           « la suite existe, elle ne tient pas », ce qui est vrai et ne
           s'inquiète pas.
           ⚠️ Le libellé complet reste porté par `aria-label` : la lecture
           d'écran n'a pas à souffrir d'un manque de place. */
        style={{ ...inputStyle, height: 38, width: '100%', paddingInlineEnd: 34, textOverflow: 'ellipsis', ...style, flex: undefined, maxWidth: undefined }}
      />
      {rempli && (
        <button
          type="button" onClick={effacer}
          title="Effacer la recherche (Échap)" aria-label="Effacer la recherche"
          style={{
            position: 'absolute', insetInlineEnd: 6, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', width: 22, height: 22,
            background: 'transparent', border: 'none', borderRadius: '50%',
            color: 'var(--text-muted)', cursor: 'pointer', padding: 0,
          }}>
          <I n="x" s={14} />
        </button>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PAGINATION — les registres qui montent tout d'un bloc.
 *
 * ── Ce qu'elle répare (relevé du 27/08/2026, P2) ────────────────────────────
 *
 *   journal RH              692 entrées   46 154 px ≈ 47 écrans
 *   maintenances            533 lignes    43 836 px ≈ 45 écrans
 *   intervalles de réf.     315 fiches    32 985 px ≈ 34 écrans
 *   paramètres              330 lignes    17 480 px ≈ 18 écrans
 *   incertitude             126 blocs     18 355 px ≈ 19 écrans
 *
 * Aucune de ces pages n'avait de pagination, et le compteur annonçait « 533 sur
 * 533 » — c'est-à-dire qu'il disait la vérité sur le filtre et rien sur ce
 * qu'on allait devoir parcourir.
 *
 * ── Ce qui est compté, et pourquoi c'est important ──────────────────────────
 *
 * Le compteur dit « 1–50 sur 533 » : la TRANCHE d'abord, le total ensuite. La
 * personne sait alors trois choses d'un coup d'œil — où elle en est, combien il
 * reste, et qu'il existe autre chose plus loin. « 533 sur 533 » ne disait aucune
 * des trois.
 *
 * ── Le piège de la page qui n'existe plus ───────────────────────────────────
 *
 * ⚠️ Filtrer pendant qu'on est page 7 laisse la page 7 d'une liste qui n'en a
 * plus que 2 : le tableau se vide, et rien ne dit pourquoi. Le hook RAMÈNE donc
 * la page dans les bornes à chaque changement de taille de liste. C'est le
 * défaut classique de toute pagination écrite à la main, et la raison pour
 * laquelle celle-ci est écrite UNE fois.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Combien de lignes par page. 50 : une page se parcourt à l'œil, et la
 *  pagination ne se déclenche pas sur les registres de taille ordinaire. */
export const PAR_PAGE = 50;

export function usePagination<T>(lignes: T[], parPage = PAR_PAGE) {
  const [page, setPage] = React.useState(1);
  const total = lignes.length;
  const pages = Math.max(1, Math.ceil(total / parPage));

  /* ⚠️ ON REVIENT PAGE 1 QUAND LA LISTE CHANGE DE TAILLE — c'est-à-dire quand
     on filtre ou qu'on cherche.

     Sans cela, filtrer depuis la page 7 laisse la page 7 d'une liste qui n'en a
     plus que deux : le tableau se vide et rien ne dit pourquoi. C'est le défaut
     classique de toute pagination écrite à la main, et la raison pour laquelle
     celle-ci est écrite une seule fois.

     Ajustement PENDANT LE RENDU et non dans un effet : c'est le motif que React
     recommande pour recaler un état sur une entrée qui change, et il évite le
     rendu en cascade qu'un `setState` dans un effet déclenche — que la règle
     `react-hooks/set-state-in-effect` du projet refuse par ailleurs. */
  const [totalVu, setTotalVu] = React.useState(total);
  if (totalVu !== total) { setTotalVu(total); setPage(1); }

  const courante = Math.min(page, pages);
  const debut = (courante - 1) * parPage;
  const visibles = React.useMemo(
    () => (total <= parPage ? lignes : lignes.slice(debut, debut + parPage)),
    [lignes, total, parPage, debut],
  );
  return {
    visibles,
    page: courante,
    pages,
    total,
    /** Rang du premier et du dernier élément affichés, en base 1. */
    debut: total === 0 ? 0 : debut + 1,
    fin: Math.min(debut + parPage, total),
    setPage,
  };
}

/**
 * La barre de pagination. Ne rend RIEN quand il n'y a qu'une page : une
 * commande qui ne commande rien ajoute du bruit à toutes les listes courtes.
 */
export function Pagination({ page, pages, debut, fin, total, onPage, quoi = 'éléments' }: {
  page: number; pages: number; debut: number; fin: number; total: number;
  onPage: (p: number) => void;
  /** Ce qu'on compte, au pluriel — « lignes », « fiches », « entrées ». */
  quoi?: string;
}) {
  if (pages <= 1) return null;
  const aller = (p: number) => {
    onPage(Math.min(pages, Math.max(1, p)));
    // Changer de page sans remonter laisse la personne au MILIEU de la
    // nouvelle : elle croit n'avoir rien changé, ou avoir sauté des lignes.
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const bouton = (libelle: React.ReactNode, cible: number, actif: boolean, aria: string) => (
    <button type="button" className="ged-btn" onClick={() => aller(cible)} disabled={!actif}
      aria-label={aria}
      style={{
        ...btnSmall('outline'),
        // Un bouton désactivé ne doit pas avoir l'air cliquable (relevé P4d).
        cursor: actif ? 'pointer' : 'not-allowed', opacity: actif ? 1 : 0.45,
        minWidth: 34, minHeight: 30, justifyContent: 'center',
      }}>
      {libelle}
    </button>
  );
  return (
    <nav aria-label="Pagination"
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      {/* `aria-live` : au clavier, changer de page ne déplace pas le focus —
          sans annonce, rien ne dit que le tableau a changé de contenu. */}
      <span aria-live="polite" style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        {debut}–{fin} sur {total} {quoi}
      </span>
      <span style={{ flex: 1 }} />
      {bouton(<I n="chevron-left" s={13} />, page - 1, page > 1, 'Page précédente')}
      <span style={{ fontSize: 12.5, color: 'var(--text-body)', fontVariantNumeric: 'tabular-nums' }}>
        page {page} / {pages}
      </span>
      {bouton(<I n="chevron-right" s={13} />, page + 1, page < pages, 'Page suivante')}
    </nav>
  );
}

export function FilterBar({ q, onQ, placeholder, chips = [], actif, onChip, total, affiches, onReset }: {
  q: string; onQ: (v: string) => void; placeholder: string;
  /** Facultatives : certains écrans n'ont qu'une recherche, sans famille à isoler. */
  chips?: { k: string; label: string; n?: number }[]; actif?: string; onChip?: (k: string) => void;
  total: number; affiches: number; onReset?: () => void;
}) {
  const filtre = !!q.trim() || (chips.length > 0 && actif !== chips[0]?.k);
  return (
    /* ⚠️ La barre porte SES DEUX marges, pas seulement celle du bas (relevé
       d'Inès du 27/08/2026 : « le cadre de recherche qui touche »). Elle suit
       toujours quelque chose — un bloc de demandes en attente, un bouton de
       création, un bandeau — et n'avait de l'air qu'en dessous : le champ
       venait se coller à la carte précédente, si bien qu'on lisait un seul bloc
       au lieu de deux. Une marge symétrique la détache des deux côtés, et le
       geste vaut pour les quarante-sept écrans qui l'emploient plutôt que
       d'être rattrapé au cas par cas. */
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '14px 0' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChampRecherche valeur={q} onChange={onQ} placeholder={placeholder} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {affiches} sur {total}
        </span>
        {filtre && onReset && (
          <button onClick={onReset} className="ged-btn" style={btnSmall('outline')}>Réinitialiser</button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {chips.map((c) => {
          const on = actif === c.k;
          return (
            <button key={c.k} onClick={() => onChip?.(c.k)} className="ged-btn" aria-pressed={on}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-sans)', fontSize: 12.5,
                fontWeight: 600, padding: '6px 12px', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
                background: on ? 'var(--cyan-50)' : 'transparent',
                color: on ? 'var(--cyan-700)' : 'var(--text-body)',
                border: `1px solid ${on ? 'var(--cyan-200)' : 'var(--border-default)'}`,
              }}>
              {/* ⚠️ LE ZÉRO S'AFFICHE (relevé SYS-2 / MAJ-05 du 26/08/2026).
                  Le compteur était masqué quand il valait 0 : « À traiter » et
                  « Sans réponse » apparaissaient alors SANS chiffre, à côté de
                  chips qui en portaient un. Un compteur absent ne se lit pas
                  « zéro », il se lit « non calculé » — et l'on clique pour
                  vérifier, ce qui est exactement le geste qu'un compteur
                  existe pour éviter.
                  Zéro est une mesure, et c'est même souvent la bonne nouvelle. */}
              {c.label}{typeof c.n === 'number' && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, opacity: c.n > 0 ? 0.75 : 0.45 }}>{c.n}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Signale un retard ou une ancienneté. `jours` négatif = échéance dépassée. */
export function LateBadge({ jours, libelleRetard = 'Retard', seuilAmbre = 0 }: { jours: number | null; libelleRetard?: string; seuilAmbre?: number }) {
  if (jours === null) return null;
  if (jours < 0) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {libelleRetard} · {Math.abs(jours)} j
      </span>
    );
  }
  if (seuilAmbre > 0 && jours <= seuilAmbre) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: 'var(--amber-50, #FFF8EC)', color: 'var(--amber-700, #8A5B00)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
        dans {jours} j
      </span>
    );
  }
  return null;
}

/** Message d'erreur de formulaire, rendu SOUS le champ concerné. */
export function FieldError({ children }: { children?: React.ReactNode }) {
  if (!children) return null;
  return <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)', marginTop: 5 }}>{children}</div>;
}

/** Légende « * champ obligatoire », à poser en tête de formulaire. */
export function RequiredHint() {
  return <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}><span style={{ color: 'var(--red-600)' }}>*</span> champ obligatoire</div>;
}
